import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { BreadCrumb } from "../components/BreadCrumb";
import { aiCorrecting, postCornerLabels, getChapters, getCorrectionStatus, sendClientErrorLog } from "../services/api";
import { isProcessingStatus } from "../hooks/useCorrectionStatus";
import { stashSubmitError, extractSubmitErrorMessage } from "../utils/uploadQuota";
import {
    EMPTY_CLIENT_ERROR_CONTEXT,
    readCurrentPage,
    reportClientError,
    reportClientErrorBatch,
    rejectedFileError,
    pdfExpandFailedError,
    unreadableImageError,
    tooManyPagesError,
    rotateImageLoadFailedError,
    submitFailedError,
    imageSubmitStats,
    type ClientErrorLogContext,
    type PendingClientError,
} from "../utils/clientErrorLog";
import { filterHiddenChapters } from "../data/hiddenChapters";
import { getUserData } from "../utils/userData";
import type { Chapter } from "../types/common";
import TopicDesc from "../components/TopicDesc";
import Swal from "sweetalert2";
import { ImageAnalyzer, type ImageAnalysis } from "../utils/imageAnalyzer";
import {
    compressImageForUpload,
    isFileDecodable,
    verifyImageDecodable,
    IMAGE_DECODE_CHECK_TIMEOUT_MS,
    bakeRotationIntoImage,
    collectProcessedFilesWithRotation,
    collectOriginSourcesWithRotation,
    collectSubmitOriginalsWithRotation,
    buildRotatedSource,
    type RotatedSource,
} from "../utils/imageUtils";
import { expandFilesWithPdf } from "../utils/pdfToImages";
import { screenUploadFiles, REJECT_CODE } from "../utils/batchFileScreen";
import { PerspectiveCorrectionWithCrop } from "../components/PerspectiveCorrectionWithCrop";
import { requestCornerDetect } from "../utils/cornerDetectApi";
import { isAutoCornerDetectionEnabled, isRotateOnlyFlowEnabled } from "../utils/featureFlags";
import { scalePointToSource, computeDownscaleFactor } from "../utils/perspectiveScale";
import {
    startCornerSession,
    applyCornerDetectOutcome,
    applyDetectedCorners,
    buildCornerLabelDraft,
    collectCornerLabelEntries,
    sendCornerLabels,
    type CornerSession,
    type CornerLabelDraft,
} from "../utils/cornerLabel";
import {
    nextRotationAngle,
    formatImageCounter,
    formatRotationAnnouncement,
    resolveImageCounter,
    advanceRotationQueue,
    resolveRotationSource,
} from "../utils/rotationUi";
import {
    createInitialImageState,
    addUploadedImages,
    applyCorrectedImage,
    cancelCurrentCorrection,
    applyCropResult,
    returnToCorrectionEditor,
    removeGroupAt,
    type CornerPoint,
    type PerspectivePoints,
    type ProcessedImageGroup,
    type ImageState,
} from "../utils/uploadImageState";
import { Button, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RotateRightIcon from '@mui/icons-material/RotateRight';

// 類型定義(CornerPoint / PerspectivePoints / ProcessedImageGroup / ImageState)
// 與圖片狀態的轉換放在 utils/uploadImageState(kanban 1003418 抽出,才測得動)

// 繪製來源:未旋轉時是原圖 Image,旋轉後是離屏 canvas。
// buildRotatedSource 已抽到 utils/imageUtils(批次的頁序預覽也要用),此處保留別名不動既有 4 處引用。
type CorrectionSource = RotatedSource;

/**
 * 把「已旋轉來源」(離屏 canvas 或原圖 <img>) 壓縮成送四角偵測 API 用的 base64 data
 * URL(只縮不放,長邊上限 1280,對齊 imageUtils.compressImageForUpload 的預設值)。
 * 這裡的來源已經是 canvas/img 而非 File,所以走 canvas.toDataURL 同步轉換,
 * 不需要 FileReader(kanban 114186:自動框選預填改打後端 API)。
 *
 * 回傳值除了 dataUrl 還帶 scale(這次實際用到的縮放比例)——perspectivePoints
 * 存的是 source 的全解析度座標系,但 API 看到的是這裡再壓縮過一次的圖,兩者座標系
 * 不同。呼叫端必須拿這個 scale 把 API 回傳的角點換算回全解析度座標系,不能假設
 * scale 恆等於 1(kanban 114186 code review:目前不會出錯只是因為選圖當下已經用
 * compressImageForUpload 壓過一次、長邊也是 1280,兩次縮放比例湊巧相等;
 * 這是沒人寫下來的隱性耦合,upstream 壓縮參數一改就會無聲錯位)。
 */
function correctionSourceToApiImage(
    source: CorrectionSource,
    maxLongSide = 1280,
    quality = 0.92
): { dataUrl: string; scale: number } {
    const width = ("naturalWidth" in source && source.naturalWidth) || source.width;
    const height = ("naturalHeight" in source && source.naturalHeight) || source.height;
    const scale = computeDownscaleFactor(width, height, maxLongSide);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("無法取得 canvas context，跳過自動框選預填");
    }
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/jpeg", quality), scale };
}

// 添加 GA 事件追蹤的輔助函數
function useGAEventTracker() {
    const trackEvent = useCallback((eventName: string, eventParams: Record<string, any> = {}) => {
        // 確保 gtag 存在
        if (typeof window !== "undefined" && (window as any).gtag) {
            (window as any).gtag("event", eventName, eventParams);
        }
    }, []);

    return { trackEvent };
}

// 認證狀態接口
interface AuthState {
    loading: boolean;
    userName: string;
    isAuthenticated: boolean;
}

// 自定義 Hook - 處理認證
function useAuthentication(): AuthState {
    const [authState, setAuthState] = useState<AuthState>({
        loading: true,
        userName: "",
        isAuthenticated: false,
    });
    const navigate = useNavigate();

    useEffect(() => {
        // 使用 OIDC 認證邏輯
        try {
            const userDataString = localStorage.getItem("oidc.user:https://id.hle.com.tw:js");
            if (!userDataString) {
                setAuthState({ loading: false, userName: "", isAuthenticated: false });
                navigate("/");
                return;
            }

            const userData = JSON.parse(userDataString);
            const userName = userData.profile?.name || "學生用戶";

            setAuthState({
                loading: false,
                userName,
                isAuthenticated: true,
            });
        } catch (error) {
            console.error("認證檢查失敗:", error);
            setAuthState({ loading: false, userName: "", isAuthenticated: false });
            navigate("/");
        }
    }, [navigate]);

    return authState;
}

// 錯誤彈窗狀態接口
interface ErrorPopupState {
    show: boolean;
    message: string;
}

// 自定義 Hook - 處理錯誤訊息
function useErrorPopup() {
    const [errorPopup, setErrorPopup] = useState<ErrorPopupState>({ show: false, message: "" });
    const { trackEvent } = useGAEventTracker();

    const showError = useCallback(
        (message: string) => {
            setErrorPopup({ show: true, message });
            trackEvent("error_shown", {
                error_message: message,
            });
        },
        [trackEvent]
    );

    const hideError = useCallback(() => {
        setErrorPopup({ show: false, message: "" });
    }, []);

    return { errorPopup, showError, hideError };
}

// 自定義 Hook - 處理圖片上傳
// DEV 實驗用：?skipCorrect=1 → 跳過透視校正/分割/AI 優化，原圖直送批改
const isSkipCorrectExperiment = (): boolean => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("skipCorrect") === "1";
};

// getClientErrorContext:前端錯誤紀錄用的身分、題目與頁面(kanban 1003072)。
// 這個 hook 宣告在 textState 之前,由頁面傳一支讀 ref 的函式進來,送紀錄的當下才取值。
function useImageUpload(getClientErrorContext: () => ClientErrorLogContext) {
    const skipCorrect = useMemo(isSkipCorrectExperiment, []);
    // 一次加入多張時兩條動線都逐張處理,但各用各的佇列:旋轉視窗走 pendingRotationGroupIds
    // (kanban 106497),四角校正+分段走 pendingCorrectionGroupIds(kanban 1003418)
    const rotateOnlyFlowForUpload = useMemo(isRotateOnlyFlowEnabled, []);
    const initialImageState = useMemo<ImageState>(createInitialImageState, []);
    
    const [imageState, setImageState] = useState<ImageState>(initialImageState);
    const { trackEvent } = useGAEventTracker();
    const [draggedCorner, setDraggedCorner] = useState<keyof PerspectivePoints | null>(null);
    const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);

    // PREVIEW_URL_CLEANUP_ON_UNMOUNT
    // 圖片預覽的物件網址清理:**只在元件卸載時**釋放(kanban 106078)。
    //
    // 原本依賴 [previewUrls, originalPreviewUrls, showPerspectiveEditor],陣列每次
    // 變動 cleanup 就拿**舊陣列**跑一次 revoke —— 加傳第二張圖時,第一張的 blob
    // 當場被釋放掉:
    //   · 縮圖看起來還正常(canvas 早畫好了,畫過的像素不受 revoke 影響)
    //   · 但按旋轉要依新角度重畫時 img.onload 永不觸發,畫面不動 =「按了沒反應」
    //   · imageRotations 其實有正確更新,所以從 state 完全看不出問題
    // 2026-08-26 QA 實測:分兩次上傳後,第一張 blob 回 load error、第二張正常。
    //
    // 個別刪圖 / 重設圖片的釋放在 removeProcessedGroup 等處已各自處理,這裡只負責
    // 卸載時收尾。依賴必須維持空陣列 —— 讀 imageState 會把依賴帶回來,改讀 ref
    // 的當下值(previewUrlCleanup.test.ts 用原始碼比對釘住這兩件事)。
    const previewUrlsRef = useRef(imageState.previewUrls);
    const originalPreviewUrlsRef = useRef(imageState.originalPreviewUrls);
    previewUrlsRef.current = imageState.previewUrls;
    originalPreviewUrlsRef.current = imageState.originalPreviewUrls;
    useEffect(() => {
        return () => {
            previewUrlsRef.current.forEach((url) => {
                if (url) URL.revokeObjectURL(url);
            });
            originalPreviewUrlsRef.current.forEach((url) => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, []);

    // 張數超標的錯誤紀錄要在 setImageState 的 updater「外面」判斷(kanban 1003072):
    // StrictMode 開發模式會把 updater 跑兩次,寫在裡面會送兩筆。選圖流程 await 了好幾輪,
    // 走到判斷時畫面早已 render 過最新的張數,這裡讀 ref 的當下值與 updater 的 prev 一致。
    const originalImageCountRef = useRef(imageState.originalImageCount);
    originalImageCountRef.current = imageState.originalImageCount;

    // 處理圖片選擇
    const handleImageChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || []);

            if (!files.length) {
                return;
            }

            trackEvent("image_selected", {
                file_count: files.length,
            });

            // 先做檔案類型/大小檢查(kanban 1000553:規則收斂到 utils/batchFileScreen,
            // 與批次上傳頁共用)。改動前這裡的 5MB 同時套在圖片與 PDF 上,老師的整班
            // 掃描 PDF(動輒數十 MB)一律被擋在門外 —— 但 PDF 不會被原樣上傳,而是
            // 逐頁展開成圖片、各自壓縮後才送出,來源檔多大跟實際上傳量幾乎無關,
            // 那道門檻擋錯了對象。圖片維持 5MB(理由見 batchFileScreen 檔頭)。
            const { accepted: rawValidFiles, rejected } = screenUploadFiles(files);
            // 三種錯誤(被略過的檔案、PDF 展開失敗、壞檔)收進同一個陣列最後一次呈現。
            // 改動前是後面的錯誤直接覆蓋前面那句,多選時只看得到最後發生的那一種。
            const uploadErrorMessages: string[] = [];
            // 同一批錯誤另外送紀錄給後端(kanban 1003072),選圖流程跑完才一次送出、不 await。
            // 訊息照抄上面顯示給學生的那一句;不 push 進 uploadErrorMessages,畫面不變。
            const clientErrors: PendingClientError[] = [];
            // 反查原始 File 取 GA 參數:rejected 只帶 name/reason/code
            const filesByName = new Map(files.map((f) => [f.name, f]));
            for (const r of rejected) {
                uploadErrorMessages.push(`${r.name}：${r.reason}`);
                clientErrors.push(
                    rejectedFileError(`${r.name}：${r.reason}`, r.code, filesByName.get(r.name))
                );
                // 事件名與 error_type 的值維持現行(image_error / invalid_file_type /
                // file_too_large),GA 上的既有報表才接得起來;逐檔發送也維持原樣。
                // 分類看 code 不比對訊息字串 —— 文案改一次就會壞。
                const original = filesByName.get(r.name);
                trackEvent(
                    "image_error",
                    r.code === REJECT_CODE.unsupported
                        ? {
                              error_type: "invalid_file_type",
                              file_type: original?.type,
                          }
                        : {
                              error_type: "file_too_large",
                              file_size:
                                  Math.round((original?.size ?? 0) / 1024 / 1024) +
                                  "MB",
                          }
                );
            }

            // PDF 逐頁展開成圖片後走既有圖片流程(kanban 106497)
            const pdfExpanded = await expandFilesWithPdf(rawValidFiles);
            // truncated(「頁數超過 N 頁,僅取前 N 頁」)在單篇不能顯示(kanban 1000825):
            // 那是 pdfToImages 依 maxPages 產生的提示,單篇沒有傳 maxPages、沿用預設的
            // 10,但單篇實際只收 2 張 —— 10 頁一進來就整批超標被擋,一張都不會留下。
            // 老師看到「僅取前 10 頁」會以為有 10 頁進去了,而清單是空的、交卷鍵是灰的。
            // 批次頁(BatchUpload)同樣把這則濾掉、改用自己的統一提示(kanban 1000645)。
            const droppedPages = pdfExpanded.errors.reduce(
                (sum, err) => sum + (err.code === "truncated" ? err.dropped ?? 0 : 0),
                0
            );
            const pdfErrorsToShow = pdfExpanded.errors.filter(
                (err) => err.code !== "truncated"
            );
            if (pdfErrorsToShow.length > 0) {
                uploadErrorMessages.push(
                    ...pdfErrorsToShow.map((err) => `${err.name}：${err.reason}`)
                );
            }
            // pdfToImages 不回傳底層原因(批次頁共用、不動它),紀錄只帶原檔的資訊與檔頭檔尾
            for (const err of pdfErrorsToShow) {
                clientErrors.push(
                    pdfExpandFailedError(
                        `${err.name}：${err.reason}`,
                        rawValidFiles.find((f) => f.name === err.name)
                    )
                );
            }

            // 壞檔驗證(kanban 113663 QA 三修):Chrome 對「檔頭完整、資料截斷」的 JPEG,
            // onload 照觸發、連 img.decode() 都會 resolve(QA 站實測)—— 靠瀏覽器解碼行為
            // 攔不到截斷檔,壞檔會一路溜進 processedGroups:佔一格、旋轉視窗全空白、還能交卷。
            // isFileDecodable 改成兩層:先驗位元組結構完整性(截斷的 JPEG/PNG 照結構走到
            // 檔尾都遇不到主影像的 FFD9/IEND;主影像之後還帶資料的手機原檔照常放行,
            // kanban 1003071),再走 decode()(對垃圾檔有效);讀檔失敗、格式不認得、
            // 環境不支援 → 一律放行(fail-open,不擋正常圖)。
            const decodeChecks = await Promise.all(
                pdfExpanded.files.map((f) => isFileDecodable(f))
            );
            const decodableFiles = pdfExpanded.files.filter((_, i) => decodeChecks[i]);
            const undecodableCount = pdfExpanded.files.length - decodableFiles.length;
            if (undecodableCount > 0) {
                // 與旋轉視窗攔截(handleRotateImageLoadFailure)同一句文案;
                // 顯示走上傳區既有的 imageError 紅字,與檔案類型/大小錯誤同一套樣式
                uploadErrorMessages.push("圖片無法讀取，請重新拍攝或選擇其他圖片");
                trackEvent("image_error", {
                    error_type: "image_decode_failed",
                    file_count: undecodableCount,
                });
                // 每個壞檔一筆;結構分析(整份讀檔)留到送紀錄時才做,只對這幾個壞檔
                pdfExpanded.files.forEach((f, i) => {
                    if (!decodeChecks[i]) {
                        clientErrors.push(
                            unreadableImageError(
                                "圖片無法讀取，請重新拍攝或選擇其他圖片",
                                f,
                                pdfExpanded.fromPdf[i]
                            )
                        );
                    }
                });
            }

            // 壓縮：長邊超過 1280 的圖片縮小，降低 canvas 負擔讓角點拖曳更順暢
            const compressed = await Promise.all(
                decodableFiles.map((f) => compressImageForUpload(f))
            );

            // TOO_MANY_PAGES_CLIENT_ERROR_LOG(kanban 1003072):張數超標的紀錄。
            // 條件與訊息照抄下面 updater 的判斷(validFiles 就是 compressed 的檔案),
            // 張數讀 ref;updater 裡不送紀錄(理由見 originalImageCountRef)。
            // clientErrorLogWiring.test.ts 比對兩段訊息的組法,改一邊要一起改。
            const incomingCountForLog = compressed.length;
            if (
                incomingCountForLog > 0 &&
                originalImageCountRef.current + incomingCountForLog > 2
            ) {
                const overflowMessageForLog =
                    incomingCountForLog > 2
                        ? `${files[0]?.name ?? "這份檔案"}：這份檔案有 ${
                              incomingCountForLog + droppedPages
                          } 頁。這裡一次只收一位學生的作文，最多 2 頁`
                        : "最多只能上傳 2 張原始圖片(PDF 每頁算 1 張)";
                clientErrors.push(
                    tooManyPagesError(overflowMessageForLog, {
                        pageCount: incomingCountForLog + droppedPages,
                        droppedPages,
                        existingImageCount: originalImageCountRef.current,
                        incomingImageCount: incomingCountForLog,
                    })
                );
            }

            setImageState((prev) => {
                const validFiles: File[] = compressed.map((c) => c.file);
                const newPreviewUrls: string[] = compressed.map((c) => c.previewUrl);
                const newImageError: string | null =
                    uploadErrorMessages.length > 0
                        ? uploadErrorMessages.join("；")
                        : null;

                if (validFiles.length > 0) {
                    // 檢查是否超過原始圖片上限(PDF 多頁展開一次可能加多張)
                    if (prev.originalImageCount + validFiles.length > 2) {
                        // 一份檔案自己就展開出超過上限的頁數 —— 幾乎一定是選錯檔(在
                        // 「一位學生一篇」的入口選到整班掃描)。訊息要講出實際頁數與這個
                        // 入口是做什麼用的,老師才知道自己選錯了(kanban 1000825)。
                        // 頁數 = 實際展開的張數 + 被 maxPages 截掉的頁數(truncated 的
                        // dropped),否則 30 頁的檔案只會報成 10 頁。
                        // 另一種超標是「已經有圖了、再加一張」,那不是選錯檔,沿用原訊息。
                        const overflowMessage =
                            validFiles.length > 2
                                ? `${files[0]?.name ?? "這份檔案"}：這份檔案有 ${
                                      validFiles.length + droppedPages
                                  } 頁。這裡一次只收一位學生的作文，最多 2 頁`
                                : "最多只能上傳 2 張原始圖片(PDF 每頁算 1 張)";
                        // 張數超標時仍要保留 newImageError:同一次選取裡可能既有檔案被擋
                        // (太大/格式不符)、剩下的又撞到張數上限。直接覆蓋會讓被擋的檔名
                        // 與原因整批消失,老師只看到「最多 2 張」,不知道還有一個檔根本
                        // 沒進來 —— 那正是卡 1000553 要修掉的「什麼都沒發生」。
                        return {
                            ...prev,
                            imageError: [newImageError, overflowMessage]
                                .filter(Boolean)
                                .join("；"),
                        };
                    }
                    
                    // 添加新圖片到列表中
                    trackEvent("image_uploaded", {
                        valid_files: validFiles.length,
                        total_images: prev.selectedImages.length + validFiles.length,
                    });

                    // 新圖片怎麼併進狀態(編輯索引、group、佇列…)見 addUploadedImages
                    return addUploadedImages(prev, {
                        validFiles,
                        newPreviewUrls,
                        imageError: newImageError,
                        rotateOnlyFlow: rotateOnlyFlowForUpload,
                        skipCorrect,
                        createGroupId: (index) => `group_${Date.now()}_${index}`, // 生成唯一ID
                    });
                }

                return {
                    ...prev,
                    imageError: newImageError,
                };
            });

            // 錯誤紀錄一次送出(最多 5 筆),不 await、失敗吞掉,畫面與流程不受影響
            void reportClientErrorBatch(sendClientErrorLog, getClientErrorContext(), clientErrors);
        },
        [trackEvent, skipCorrect, rotateOnlyFlowForUpload, getClientErrorContext]
    );



    // 設定透視校正點
    const setPerspectivePoints = useCallback((points: PerspectivePoints | null) => {
        setImageState(prev => {
            const newArray = [...prev.perspectivePointsArray];
            newArray[prev.currentEditingImageIndex] = points;
            return {
                ...prev,
                perspectivePointsArray: newArray
            };
        });
    }, []);

    // 設定處理後的圖片
    const setProcessedImageUrl = useCallback((url: string | null) => {
        setImageState(prev => {
            const newArray = [...prev.processedImageUrlArray];
            newArray[prev.currentEditingImageIndex] = url;
            
            // 使用 ID-based 系統更新 ProcessedImageGroup 中的校正後圖片
            const newProcessedGroups = [...prev.processedGroups];
            // 校正視窗的旋轉已烘進校正輸出;預覽區若還留著舊角度會再轉一次(kanban 113663 / 對應國中 108395)
            const newImageRotations = { ...prev.imageRotations };

            console.log('設定校正後圖片:');
            console.log('- currentProcessingGroupId:', prev.currentProcessingGroupId);
            console.log('- processedGroups 長度:', newProcessedGroups.length);
            console.log('- 校正後的URL:', url);
            
            if (url && prev.currentProcessingGroupId) {
                // 根據 ID 找到正確的群組
                const groupIndex = newProcessedGroups.findIndex(group => group.id === prev.currentProcessingGroupId);
                if (groupIndex >= 0) {
                    console.log('- 找到群組索引:', groupIndex);
                    console.log('- 更新群組前:', newProcessedGroups[groupIndex]);
                    newProcessedGroups[groupIndex] = {
                        ...newProcessedGroups[groupIndex],
                        correctedUrl: url
                    };
                    // 這張的新校正輸出已是轉正後的像素 → 預覽旋轉角度歸零,避免交卷時再烤一次
                    delete newImageRotations[newProcessedGroups[groupIndex].id];
                    console.log('- 更新群組後:', newProcessedGroups[groupIndex]);
                } else {
                    console.log('- 錯誤：找不到群組ID為', prev.currentProcessingGroupId, '的群組');
                }
            } else {
                console.log('- 錯誤：URL為空或currentProcessingGroupId為空');
            }
            
            return {
                ...prev,
                processedImageUrlArray: newArray,
                processedGroups: newProcessedGroups,
                imageRotations: newImageRotations
            };
        });
    }, []);

    // 清除初始化標記
    const clearInitializationFlag = useCallback(() => {
        setImageState(prev => ({
            ...prev,
            needsInitialization: false
        }));
    }, []);

    // 關閉透視校正編輯器 - 移除「當下這一張」;佇列裡還有下一張就接著開(kanban 1003418)
    const closePerspectiveEditor = useCallback(() => {
        setImageState(prev => {
            // 如果原始圖片數量大於1，表示還有別張要保留，只移除當下這一張
            if (prev.originalImageCount > 1) {
                // 只移除當下這一張,同一批還有沒處理的圖就接著開下一張(規則見 cancelCurrentCorrection)
                const { state, revokeUrls } = cancelCurrentCorrection(prev, "perspective");
                revokeUrls.forEach((url) => URL.revokeObjectURL(url));
                return state;
            } else {
                // 如果只有一張原始圖片，關閉編輯器並清理所有圖片狀態
                // 釋放所有 URL 資源
                prev.originalPreviewUrls.forEach(url => {
                    if (url) URL.revokeObjectURL(url);
                });
                prev.previewUrls.forEach(url => {
                    if (url) URL.revokeObjectURL(url);
                });
                prev.processedImageUrlArray.forEach(url => {
                    if (url) URL.revokeObjectURL(url);
                });

                return {
                    ...prev,
                    showPerspectiveEditor: false,
                    // 清理所有圖片相關狀態
                    originalSelectedImages: [],
                    originalPreviewUrls: [],
                    selectedImages: [],
                    previewUrls: [],
                    processedGroups: [],
                    originalImageCount: 0,
                    currentEditingImageIndex: 0,
                    processedImageUrlArray: [],
                    perspectivePointsArray: [],
                    perspectiveManuallyAdjusted: [],
                    imageRotations: {},
                    pendingCorrectionGroupIds: [],
                    pendingCornerLabel: null,
                };
            }
        });
    }, []);

    // 應用透視校正後的圖片
    // groupId:按「下一步」當下正在校正的 group(kanban 1003418)。這裡要等 fetch 才寫回,
    // 寫的位置是寫回當下的 currentEditingImageIndex;多張逐張處理時若這段期間已換到
    // 下一張,照寫會把這張的校正圖蓋到下一張、還把下一張的視窗關掉 —— 換張了就放棄寫回。
    // cornerLabel:按「下一步」當下抓下來的四角標註(kanban 1003468),跟著校正圖一起寫回,
    // 換張時一併丟掉;分段完成才搬進 group(規則見 applyCorrectedImage / applyCropResult)
    const applyProcessedImage = useCallback((processedUrl: string, groupId: string | null, cornerLabel?: CornerLabelDraft | null) => {
        fetch(processedUrl)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], 'corrected_image.jpg', { type: 'image/jpeg' });
                const newUrl = URL.createObjectURL(blob);

                // 換張了就放棄寫回;原圖預覽 URL 不釋放(規則見 applyCorrectedImage)
                setImageState(prev => {
                    const { state, revokeUrls } = applyCorrectedImage(prev, { groupId, file, url: newUrl, cornerLabel });
                    revokeUrls.forEach((url) => URL.revokeObjectURL(url));
                    return state;
                });
            });
    }, []);

    // 銳化濾鏡輔助函數
    const applySharpen = useCallback((imageData: ImageData, intensity: number): ImageData => {
        const data = imageData.data;
        const width = imageData.width;
        const height = imageData.height;
        const output = new Uint8ClampedArray(data);

        const kernel = [
            0, -intensity / 100, 0,
            -intensity / 100, 1 + 4 * intensity / 100, -intensity / 100,
            0, -intensity / 100, 0
        ];

        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                for (let c = 0; c < 3; c++) {
                    let sum = 0;
                    for (let ky = -1; ky <= 1; ky++) {
                        for (let kx = -1; kx <= 1; kx++) {
                            const idx = ((y + ky) * width + (x + kx)) * 4 + c;
                            const kernelIdx = (ky + 1) * 3 + (kx + 1);
                            sum += data[idx] * kernel[kernelIdx];
                        }
                    }
                    const outputIdx = (y * width + x) * 4 + c;
                    output[outputIdx] = Math.max(0, Math.min(255, sum));
                }
            }
        }

        return new ImageData(output, width, height);
    }, []);

    // AI 自動圖片優化
    const autoOptimizeImage = useCallback(async (file: File): Promise<File> => {
        try {

            // 1. AI 分析圖片
            const analyzer = new ImageAnalyzer();
            const analysis: ImageAnalysis = await analyzer.analyzeImage(file);


            // 2. 如果沒有需要優化的地方，直接返回原圖
            const { recommendations } = analysis;
            const needsOptimization =
                Math.abs(recommendations.brightness) > 5 ||
                Math.abs(recommendations.contrast) > 5 ||
                Math.abs(recommendations.sharpness) > 5 ||
                Math.abs(recommendations.rotation) > 1 ||
                Math.abs(recommendations.saturation) > 5;

            if (!needsOptimization) {
                return file;
            }


            // 3. 創建 canvas 並載入圖片
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('無法創建 canvas context');

            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                const image = new Image();
                const url = URL.createObjectURL(file);

                image.onload = () => {
                    URL.revokeObjectURL(url);
                    resolve(image);
                };

                image.onerror = () => {
                    URL.revokeObjectURL(url);
                    reject(new Error('圖片載入失敗'));
                };

                image.src = url;
            });

            // 4. 設定 canvas 尺寸
            canvas.width = img.width;
            canvas.height = img.height;

            // 5. 應用旋轉
            if (Math.abs(recommendations.rotation) > 0.5) {
                ctx.save();
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate((recommendations.rotation * Math.PI) / 180);
                ctx.translate(-canvas.width / 2, -canvas.height / 2);
            }

            // 6. 繪製圖片
            ctx.drawImage(img, 0, 0);

            if (Math.abs(recommendations.rotation) > 0.5) {
                ctx.restore();
            }

            // 7. 取得圖片數據並應用處理
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;

            // 8. 應用亮度、對比度、飽和度調整
            for (let i = 0; i < data.length; i += 4) {
                let r = data[i];
                let g = data[i + 1];
                let b = data[i + 2];

                // 亮度調整
                r += recommendations.brightness;
                g += recommendations.brightness;
                b += recommendations.brightness;

                // 對比度調整
                if (Math.abs(recommendations.contrast) > 0.5) {
                    const contrastFactor = (259 * (recommendations.contrast + 255)) / (255 * (259 - recommendations.contrast));
                    r = contrastFactor * (r - 128) + 128;
                    g = contrastFactor * (g - 128) + 128;
                    b = contrastFactor * (b - 128) + 128;
                }

                // 飽和度調整
                if (Math.abs(recommendations.saturation) > 0.5) {
                    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
                    r = gray + (r - gray) * (1 + recommendations.saturation / 100);
                    g = gray + (g - gray) * (1 + recommendations.saturation / 100);
                    b = gray + (b - gray) * (1 + recommendations.saturation / 100);
                }

                // 確保值在 0-255 範圍內
                data[i] = Math.max(0, Math.min(255, r));
                data[i + 1] = Math.max(0, Math.min(255, g));
                data[i + 2] = Math.max(0, Math.min(255, b));
            }

            // 9. 應用銳化（如果需要）
            let finalImageData = imageData;
            if (recommendations.sharpness > 0.5) {
                finalImageData = applySharpen(imageData, recommendations.sharpness);
            }

            // 10. 將處理後的數據放回 canvas
            ctx.putImageData(finalImageData, 0, 0);

            // 11. 轉換為 blob 並創建新的 File
            const optimizedBlob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((blob) => {
                    resolve(blob!);
                }, 'image/jpeg', 0.9);
            });

            const optimizedFile = new File([optimizedBlob], `optimized_${file.name}`, {
                type: 'image/jpeg',
                lastModified: Date.now()
            });

            return optimizedFile;

        } catch (error) {
            console.error('圖片優化失敗:', error);
            // 如果優化失敗，返回原圖
            return file;
        }
    }, [applySharpen]);

    // 開啟分割編輯器
    const openCropEditor = useCallback(() => {
        setImageState(prev => ({
            ...prev,
            showCropEditor: true
        }));
    }, []);

    // 處理分割完成
    // groupId:分割編輯器開啟時正在處理的 group(kanban 1003418)。分割編輯器在圖片載入、
    // toBlob 完成後才非同步呼叫這裡,期間「確認分段」可再按一次(連點)、也可按 X。
    // 多張逐張處理時,第一次回呼已經換到下一張,第二次回呼若照 currentProcessingGroupId
    // 寫回,會把上一張的細條蓋到下一張、下一張也被當成已分段 —— 換張了或那張已被取消
    // 就丟掉這次結果。
    const handleCropComplete = useCallback((images: Blob[], groupId?: string | null) => {
        // 反轉圖片順序，因為中文作文是從右到左書寫
        const reversedImages = [...images].reverse();
        const imageUrls = reversedImages.map(blob => URL.createObjectURL(blob));

        // 將 Blob 轉換為 File 物件
        const imageFiles = reversedImages.map((blob, index) =>
            new File([blob], `cropped_image_${Date.now()}_${index + 1}.jpg`, { type: 'image/jpeg' })
        );

        setImageState(prev => {
            const { state, revokeUrls } = applyCropResult(prev, {
                groupId,
                files: imageFiles,
                urls: imageUrls,
            });
            revokeUrls.forEach((url) => URL.revokeObjectURL(url));
            return state;
        });
    }, []);

    // 關閉分割編輯器 - 移除「當下這一張」;佇列裡還有下一張就接著開(kanban 1003418)
    const closeCropEditor = useCallback(() => {
        setImageState(prev => {
            // 如果原始圖片數量大於1，表示還有別張要保留，只移除當下這一張
            if (prev.originalImageCount > 1) {
                // 只移除當下這一張,同一批還有沒處理的圖就接著開下一張(規則見 cancelCurrentCorrection)
                const { state, revokeUrls } = cancelCurrentCorrection(prev, "crop");
                revokeUrls.forEach((url) => URL.revokeObjectURL(url));
                return state;
            } else {
                // 如果只有一張原始圖片，關閉編輯器並清理所有圖片狀態
                // 釋放所有 URL 資源
                prev.originalPreviewUrls.forEach(url => {
                    if (url) URL.revokeObjectURL(url);
                });
                prev.previewUrls.forEach(url => {
                    if (url) URL.revokeObjectURL(url);
                });
                prev.processedImageUrlArray.forEach(url => {
                    if (url) URL.revokeObjectURL(url);
                });
                // 釋放已分割的圖片 URLs
                prev.processedGroups.forEach(group => {
                    if (group.processedUrls) {
                        group.processedUrls.forEach(url => {
                            if (url) URL.revokeObjectURL(url);
                        });
                    }
                    if (group.originalUrl) {
                        URL.revokeObjectURL(group.originalUrl);
                    }
                    if (group.correctedUrl) {
                        URL.revokeObjectURL(group.correctedUrl);
                    }
                });

                return {
                    ...prev,
                    showCropEditor: false,
                    // 清理所有圖片相關狀態
                    originalSelectedImages: [],
                    originalPreviewUrls: [],
                    selectedImages: [],
                    previewUrls: [],
                    processedGroups: [],
                    originalImageCount: 0,
                    currentEditingImageIndex: 0,
                    processedImageUrlArray: [],
                    perspectivePointsArray: [],
                    perspectiveManuallyAdjusted: [],
                    // 清理分割相關狀態
                    croppedBlobs: [],
                    imageRotations: {},
                    pendingCorrectionGroupIds: [],
                    pendingCornerLabel: null,
                };
            }
        });
    }, []);

    // 從分割編輯器回到透視校正編輯器
    const goBackToPerspectiveEditor = useCallback(() => {
        // 從這一張的 group 重建調整邊界視窗(規則與原因見 returnToCorrectionEditor)
        setImageState(prev => returnToCorrectionEditor(prev));
    }, []);

    // 預覽區「向右旋轉90度」:只改角度狀態,交卷時才由 bakeRotationIntoImage 烤進像素。
    // 旋轉視窗(kanban 113663)也走這條:兩處共用同一份 imageRotations,不會打架。
    const rotateGroupImage = useCallback((groupId: string) => {
        setImageState(prev => ({
            ...prev,
            imageRotations: {
                ...prev.imageRotations,
                [groupId]: nextRotationAngle(prev.imageRotations[groupId]),
            },
        }));
    }, []);

    // 旋轉視窗按「確認」:角度已寫進 imageRotations,這裡只要關掉視窗。
    // 刻意不烤新的 dataURL、也不進分割模式 —— 上傳頁縮圖的 CSS transform 會直接顯示
    // 轉正後的方向,交卷時才由 bakeRotationIntoImage 把角度烤進像素(kanban 113663)。
    const confirmRotation = useCallback(() => {
        setImageState(prev => ({ ...prev, showPerspectiveEditor: false }));
    }, []);

    // 旋轉視窗按「確認」:處理完當前這張就換下一張,整批走完才關窗(kanban 106497)。
    // 與 confirmRotation 分開:後者是「單純關窗」的語意,壞檔移除那條路徑還在用。
    const confirmRotationStep = useCallback(() => {
        setImageState(prev => {
            // 先把已經不存在的 group 濾掉再前進(kanban 106497 code review):
            // 除了 removeProcessedGroup,還有幾條路徑會清掉 processedGroups
            // (closePerspectiveEditor / closeCropEditor / handleCropComplete 的全清分支),
            // 在這裡以 processedGroups 為準防守,就不必逐一去補那些呼叫點。
            const alive = prev.pendingRotationGroupIds.filter((id) =>
                prev.processedGroups.some((group) => group.id === id)
            );
            const next = advanceRotationQueue(alive, prev.currentProcessingGroupId);
            return {
                ...prev,
                pendingRotationGroupIds: next.pending,
                // 走完了就維持在最後一張,不清掉 —— 關窗那一 render 若掉回索引推導,
                // 畫布會閃一下別張圖。該值只被視窗內的 rotationGroupId 讀,
                // 下次上傳就會被覆蓋。
                currentProcessingGroupId: next.done
                    ? prev.currentProcessingGroupId
                    : next.currentId,
                showPerspectiveEditor: next.done ? false : prev.showPerspectiveEditor,
            };
        });
    }, []);

    return {
        imageState,
        setImageState,
        handleImageChange,
        setPerspectivePoints,
        setProcessedImageUrl,
        applyProcessedImage,
        autoOptimizeImage,
        draggedCorner,
        setDraggedCorner,
        dragOffset,
        setDragOffset,
        clearInitializationFlag,
        closePerspectiveEditor,
        openCropEditor,
        handleCropComplete,
        closeCropEditor,
        goBackToPerspectiveEditor,
        rotateGroupImage,
        confirmRotation,
        confirmRotationStep
    };
}

// 文字狀態接口
interface TextState {
    essayText: string;
    essayTitle: string;
    textError: string | null;
    unitId: string;
    selectedTopic: string;
}

// 自定義 Hook - 處理文字輸入
function useTextUpload() {
    const [textState, setTextState] = useState<TextState>({
        essayText: "",
        essayTitle: "",
        textError: null,
        unitId: "L5 - 微物之處有深情──如何描寫體悟",
        selectedTopic: "",
    });
    const { trackEvent } = useGAEventTracker();

    // 處理文字輸入變更
    const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTextState((prev) => ({
            ...prev,
            essayText: e.target.value,
            textError: null,
        }));
    }, []);

    // 直接設置文字內容的函數（用於重試）
    const setEssayText = useCallback((text: string) => {
        setTextState((prev) => ({
            ...prev,
            essayText: text,
            textError: null,
        }));
    }, []);

    // 處理選擇題目
    const handleSelectTopic = useCallback(
        (chapterId: string, chapterName: string) => {
            if (chapterId) {
                trackEvent("topic_selected", {
                    chapter_id: chapterId,
                    chapter_name: chapterName,
                });
            }

            setTextState((prev) => ({
                ...prev,
                selectedTopic: chapterId,
                unitId: chapterName,
            }));
        },
        [trackEvent]
    );

    return { textState, handleTextChange, handleSelectTopic, setEssayText };
}

// API狀態接口
interface ApiState {
    uploadResult: unknown;
    workflowResult: unknown;
    uploadRequestData: unknown;
    workflowRequestData: unknown;
}

// 自定義 Hook - 處理 API 請求
function useApiRequests(bookId: string | null, selectedTopic: string, chapterOptions: Chapter[], classCode: string = "", className: string = "") {
    const [apiState, setApiState] = useState<ApiState>({
        uploadResult: null,
        workflowResult: null,
        uploadRequestData: null,
        workflowRequestData: null,
    });
    const { trackEvent } = useGAEventTracker();

    // 上傳圖片到批改 API
    const uploadImageToDify = useCallback(
        async (
            imageFiles: File[],
            processedGroups: ProcessedImageGroup[] = [],
            imageRotations: Record<string, number> = {},
            // imageFiles 全部是前端已校正＋分段的細條時才傳 true(kanban 1003418),
            // 原樣交給 aiCorrecting 帶 clientCorrected=1,判斷在 handleSubmitEssay
            clientCorrected = false
        ) => {
            // 輔助函數：將 URL 轉換為 File 物件
            const urlToFile = async (url: string, filename: string): Promise<File> => {
                const response = await fetch(url);
                const blob = await response.blob();
                return new File([blob], filename, { type: blob.type });
            };

            try {
                if (!imageFiles.length) throw new Error("無圖片可上傳");

                // 獲取用戶資料
                const userData = getUserData();
                if (!userData) {
                    throw new Error("無法獲取用戶資料");
                }

                // 🔍 LOG: 檢查用戶資料
                console.log('📝 [圖片批改] 準備送出批改，用戶資料：', {
                    sub: userData.sub,
                    name: userData.name,
                    email: userData.email,
                    isNameUUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userData.name || '')
                });

                trackEvent("api_request_started", {
                    request_type: "image_upload",
                    image_count: imageFiles.length,
                });

                // 準備整頁原稿（originFiles）。後端存進 studentInputOriginUrl，Feedback 頁、
                // 匯出 PDF、班級成績單 ZIP 三處顯示都優先用它 —— 所以這條也必須套旋轉，
                // 否則 AI 收到轉正的稿子、老師和學生看到的還是歪的（kanban 113663）。
                // 逐張 await 不用 Promise.all：多張大圖同時 decode + 重編碼會吃爆手機記憶體。
                const originFiles: File[] = [];
                for (const entry of collectOriginSourcesWithRotation(processedGroups, imageRotations)) {
                    let file = entry.originalFile;
                    if (entry.correctedUrl) {
                        try {
                            file = await urlToFile(entry.correctedUrl, `corrected_${entry.id}.jpg`);
                        } catch (error) {
                            console.warn(`無法轉換校正後圖片 ${entry.id}:`, error);
                            // 校正後圖片轉換失敗 → fail-open 退回原始檔案
                        }
                    }
                    // 角度 0 時 bakeRotationIntoImage 直接回傳原 File，不重編碼；
                    // 走過校正的 group 角度已歸零，這裡等同 no-op。
                    originFiles.push(await bakeRotationIntoImage(file, entry.angle));
                }

                // 使用統一的 aiCorrecting API
                // 從 chapterOptions 中找到對應的章節，使用 chapter 欄位作為 chapterId
                let chapterId = "1";
                if (selectedTopic) {
                    const selectedChapter = chapterOptions.find(ch => ch._id === selectedTopic);
                    chapterId = selectedChapter?.chapter || "1";
                }

                // 🔍 LOG: 記錄實際送出的資料
                console.log('📤 [圖片批改] 正在送出 API 請求，參數：', {
                    studentId: userData.sub,
                    studentName: userData.name,
                    studentEmail: userData.email,
                    bookId: bookId || "1",
                    chapterId: chapterId,
                    classCode: classCode,
                    className: className,
                    imageFilesCount: imageFiles?.length || 0,
                    originFilesCount: originFiles?.length || 0
                });

                const response = await aiCorrecting(
                    userData.sub,
                    bookId || "1", // 使用實際的 bookId
                    chapterId, // 使用章節名稱
                    userData.name,
                    userData.email,
                    classCode,
                    className,
                    "", // studentInput - 圖片模式不需要
                    imageFiles, // 分割後的圖片（原本的files）
                    originFiles, // 校正後的圖片（新增的originFiles）
                    clientCorrected
                );

                // 四角標註(kanban 1003468):只有切好細條的手動動線、拿到 ticket 之後才送,
                // 不 await;sendCornerLabels 自己吞掉所有錯誤,這裡再包一層,
                // 標註怎麼失敗都不影響上面已成功的送件
                if (clientCorrected) {
                    try {
                        const ticket = response?.data?.ticket;
                        const ticketId = typeof ticket === "string" || typeof ticket === "number" ? String(ticket) : "";
                        const labelEntries = collectCornerLabelEntries(processedGroups);
                        if (ticketId && labelEntries.length > 0) {
                            void sendCornerLabels({
                                ticketId,
                                studentId: userData.sub,
                                entries: labelEntries,
                                post: postCornerLabels,
                            });
                        }
                    } catch (error) {
                        console.warn("[cornerlabel] 四角標註準備失敗(不影響批改):", error);
                    }
                }
                


                trackEvent("api_request_completed", {
                    request_type: "image_upload",
                    success: true,
                    response_type: "json",
                });

                
                return response.data;
            } catch (error: unknown) {
                console.error("上傳圖片與批改失敗:", error);
                const errorMessage = error instanceof Error ? error.message : String(error);

                trackEvent("api_error", {
                    request_type: "image_upload",
                    error_message: errorMessage,
                });

                throw error;
            }
        },
        [trackEvent, bookId, selectedTopic, chapterOptions, classCode, className]
    );

    // 執行文字作文批改流程
    const runWorkflow = useCallback(
        async (options: { text?: string; unitId?: string; topicId?: string } = {}) => {
            try {
                // 獲取用戶資料
                const userData = getUserData();
                if (!userData) {
                    throw new Error("無法獲取用戶資料");
                }

                // 🔍 LOG: 檢查用戶資料
                console.log('📝 [文字批改] 準備送出批改，用戶資料：', {
                    sub: userData.sub,
                    name: userData.name,
                    email: userData.email,
                    isNameUUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userData.name || '')
                });

                trackEvent("api_request_started", {
                    request_type: "text_correction",
                    text_length: options.text?.length || 0,
                    unit_id: options.unitId,
                    has_topic: Boolean(options.topicId),
                });

                // 使用統一的 aiCorrecting API
                // 從 chapterOptions 中找到對應的章節，使用 chapter 欄位作為 chapterId
                let chapterId = "1";
                if (options.topicId) {
                    const selectedChapter = chapterOptions.find(ch => ch._id === options.topicId);
                    chapterId = selectedChapter?.chapter || "1";
                }

                // 🔍 LOG: 記錄實際送出的資料
                console.log('📤 [文字批改] 正在送出 API 請求，參數：', {
                    studentId: userData.sub,
                    studentName: userData.name,
                    studentEmail: userData.email,
                    bookId: bookId || "1",
                    chapterId: chapterId,
                    classCode: classCode,
                    className: className,
                    textLength: options.text?.length || 0
                });

                const response = await aiCorrecting(
                    userData.sub,
                    bookId || "1", // 使用實際的 bookId
                    chapterId, // 使用處理後的章節 ID
                    userData.name,
                    userData.email,
                    classCode,
                    className,
                    options.text, // studentInput - 文字內容
                    undefined // files - 文字模式不需要文件
                );


                trackEvent("api_request_completed", {
                    request_type: "text_correction",
                    success: true,
                    response_type: "json",
                });

                
                return response.data;
            } catch (error: unknown) {
                console.error("執行文字批改流程失敗:", error);
                const errorMessage = error instanceof Error ? error.message : String(error);

                trackEvent("api_error", {
                    request_type: "text_correction",
                    error_message: errorMessage,
                });

                throw error;
            }
        },
        [trackEvent, bookId, chapterOptions, classCode, className]
    );

    const resetApiState = useCallback(() => {
        setApiState({
            uploadResult: null,
            workflowResult: null,
            uploadRequestData: null,
            workflowRequestData: null,
        });
    }, []);

    return {
        apiState,
        uploadImageToDify,
        runWorkflow,
        resetApiState,
        setApiState,
    };
}

export default function UploadEssayPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const bookName = searchParams.get("bookName");
    const bookId = searchParams.get("bookId");
    const classCode = searchParams.get("classCode") || "";
    const className = searchParams.get("className") || "";
    const [uploadMode, setUploadMode] = useState<"image" | "text">("image");
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 交卷前的圖片處理提示(kanban 113663 QA 追修):烤旋轉+AI 優化在低階機上要跑數秒,
    // 沒有提示學生會以為當掉。true 時顯示全螢幕「圖片處理中請稍候」鎖定畫面,
    // 文案/樣式沿用 BatchUpload 交卷前的 prepareProgress 橫幅(單篇最多 2 張,不帶 n/m 計數)。
    const [isPreparingImages, setIsPreparingImages] = useState(false);
    const [chapterOptions, setChapterOptions] = useState<Chapter[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [processingMessage, setProcessingMessage] = useState<string>("");
    // const [isLoadingChapters, setIsLoadingChapters] = useState(true); // 不再使用
    const { trackEvent } = useGAEventTracker();

    // 獲取用戶資料
    const userData = getUserData();

    // 前端錯誤紀錄(kanban 1003072)的身分與題目:ref 在下面 textState 就緒後逐次 render 更新,
    // getClientErrorContext 在送紀錄的當下取值並補上目前頁面。選圖 hook 宣告在 textState
    // 之前,所以用這個方式讓它也拿得到題目。
    const clientErrorContextRef = useRef<ClientErrorLogContext>(EMPTY_CLIENT_ERROR_CONTEXT);
    const getClientErrorContext = useCallback(
        (): ClientErrorLogContext => ({ ...clientErrorContextRef.current, page: readCurrentPage() }),
        []
    );

    // 使用自定義 Hooks
    const { loading, isAuthenticated } = useAuthentication();
    const { errorPopup, showError, hideError } = useErrorPopup();
    const {
        imageState,
        setImageState,
        handleImageChange,
        setPerspectivePoints,
        setProcessedImageUrl,
        applyProcessedImage,
        autoOptimizeImage,
        draggedCorner,
        setDraggedCorner,
        dragOffset,
        setDragOffset,
        clearInitializationFlag,
        closePerspectiveEditor,
        handleCropComplete,
        closeCropEditor,
        goBackToPerspectiveEditor,
        rotateGroupImage,
        confirmRotation,
        confirmRotationStep
    } = useImageUpload(getClientErrorContext);
    const { textState, handleTextChange, handleSelectTopic, setEssayText } = useTextUpload();
    const {
        uploadImageToDify,
        runWorkflow,
        resetApiState,
        setApiState,
    } = useApiRequests(bookId, textState.selectedTopic, chapterOptions, classCode, className);

    // 題目用 selectedTopic(chapter._id)對回 chapterOptions;textState.unitId 的初始值是
    // 寫死的舊題目名稱,不能拿來當紀錄
    clientErrorContextRef.current = {
        studentID: userData.sub,
        classCode,
        className,
        bookID: bookId ?? "",
        chapterID: textState.selectedTopic,
        chapter: chapterOptions.find((ch) => ch._id === textState.selectedTopic)?.chapter ?? "",
        uploadMode,
        page: "",
    };

    // 獲取章節資料
    useEffect(() => {
        if (bookId && userData?.sub) {
            // setIsLoadingChapters(true); // 不再使用
            getChapters(bookId, userData.sub)
                .then((res) => {
                    setChapterOptions(filterHiddenChapters(res.data || []));
                })
                .catch((err) => {
                    console.error('獲取章節失敗:', err);
                    showError('獲取題目失敗，請稍後再試');
                })
                .finally(() => {
                    // setIsLoadingChapters(false); // 不再使用
                });
        }
    }, [bookId, userData?.sub, showError]);

    // 檢查批改狀態的 useEffect - 支援輪詢
    useEffect(() => {
        let pollInterval: NodeJS.Timeout;

        const checkProcessingStatus = async (isInitialCheck = false) => {
            if (!userData?.sub) return false;

            try {
                const response = await getCorrectionStatus(userData.sub);
                
                if (response.data && Array.isArray(response.data)) {
                    // 檢查是否有處理中的票據（準備中／圖片辨識中／批改中，與列表頁同一份清單，kanban 1000823）
                    const processingTickets = response.data.filter((ticket: Record<string, unknown>) =>
                        isProcessingStatus(String(ticket.status ?? ''))
                    );
                    
                    const hasProcessingTickets = processingTickets.length > 0;
                    
                    if (hasProcessingTickets) {
                        setIsProcessing(true);
                        setProcessingMessage("評改中，請等待完成後再上傳新的作文");
                    } else {
                        setIsProcessing(false);
                        setProcessingMessage("");
                    }
                    
                    // 如果是初始檢查且沒有批改中的票據，不啟動輪詢
                    if (isInitialCheck && !hasProcessingTickets) {
                        return false;
                    }
                    
                    // 如果沒有批改中的票據，停止輪詢
                    if (!hasProcessingTickets) {
                        return false;
                    }
                    
                    return true; // 繼續輪詢
                } else {
                    setIsProcessing(false);
                    setProcessingMessage("");
                    return false; // 停止輪詢
                }
            } catch (error) {
                console.error('檢查批改狀態失敗:', error);
                // 如果 API 失敗，允許用戶繼續操作
                setIsProcessing(false);
                setProcessingMessage("");
                return true; // 繼續輪詢，可能是網路問題
            }
        };

        // 立即執行一次初始檢查
        checkProcessingStatus(true).then((shouldContinuePolling) => {
            if (shouldContinuePolling) {
                // 只有當需要繼續輪詢時才啟動 interval
                pollInterval = setInterval(async () => {
                    const continuePolling = await checkProcessingStatus(false);
                    if (!continuePolling && pollInterval) {
                        clearInterval(pollInterval);
                    }
                }, 20000); // 每20秒檢查一次
            }
        });

        // 清理函數
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [userData?.sub]);

    // 處理重試資料的 useEffect
    useEffect(() => {
        if (chapterOptions.length > 0) {
            // 檢查是否有重試資料
            const retryMode = sessionStorage.getItem('retryMode');
            const retryEssayText = sessionStorage.getItem('retryEssayText');
            const urlParams = new URLSearchParams(window.location.search);
            const urlChapterId = urlParams.get('chapterId');

            if (retryMode) {
                // 如果有指定章節，自動選擇
                if (urlChapterId) {
                    const chapter = chapterOptions.find(ch => ch._id === urlChapterId);
                    if (chapter) {
                        handleSelectTopic(urlChapterId, chapter.chapter);
                    }
                }

                // 處理文字重試
                if (retryMode === 'text' && retryEssayText) {
                    setUploadMode('text');
                    
                    // 使用 setEssayText 來設置文字內容
                    setTimeout(() => {
                        setEssayText(retryEssayText);
                    }, 100);
                    
                    // 清理重試資料
                    sessionStorage.removeItem('retryEssayText');
                } else if (retryMode === 'image') {
                    setUploadMode('image');
                }
                
                // 清理重試標記
                sessionStorage.removeItem('retryMode');
            }
        }
    }, [chapterOptions, handleSelectTopic, setEssayText, setUploadMode]);

    // 當章節資料載入完成後，恢復之前選擇的題目 - 已移除
    // useEffect(() => {
    //   if (chapterOptions.length > 0) {
    //     const savedSelectedTopic = sessionStorage.getItem('selectedTopic');
    //     if (savedSelectedTopic) {
    //       const savedChapter = chapterOptions.find(ch => ch._id === savedSelectedTopic);
    //       if (savedChapter) {
    //         handleSelectTopic(savedSelectedTopic, savedChapter.chapter);
    //       }
    //     }
    //   }
    // }, [chapterOptions, handleSelectTopic]);

    // 頁面載入時追蹤頁面瀏覽
    useEffect(() => {
        trackEvent("page_view", {
            page_title: "上傳作文頁面",
            page_location: window.location.href,
            page_path: "/upload-essay",
            book_name: bookName || "",
        });
    }, [trackEvent, bookName]);

    // 切換上傳模式（圖片/文字）
    const toggleUploadMode = useCallback(
        (mode: "image" | "text") => {
            trackEvent("upload_mode_changed", {
                previous_mode: uploadMode,
                new_mode: mode,
            });

            setUploadMode(mode);
            setIsSubmitting(false);
            resetApiState();
            hideError();
        },
        [uploadMode, resetApiState, hideError, trackEvent]
    );

    // 重設處理組中的特定圖片
    const removeProcessedGroup = useCallback(
        (groupIndex: number) => {
            trackEvent("processed_group_removed", {
                group_index: groupIndex,
            });

            setImageState((prev) => {
                // 移除該組並釋放它的物件 URLs(規則見 removeGroupAt)
                const { state, revokeUrls } = removeGroupAt(prev, groupIndex);
                revokeUrls.forEach((url) => URL.revokeObjectURL(url));
                return state;
            });
        },
        [trackEvent, setImageState]
    );

    // 建立提交按鈕的禁用狀態 memo
    const isSubmitDisabled = useMemo(() => {
        return (
            isSubmitting ||
            isProcessing ||
            (uploadMode === "image" && (!imageState.selectedImages.length || !textState.selectedTopic)) ||
            (uploadMode === "text" && !textState.essayText.trim())
        );
    }, [
        isSubmitting,
        isProcessing,
        uploadMode,
        imageState.selectedImages.length,
        textState.selectedTopic,
        textState.essayText,
    ]);

    // 處理作文提交
    const handleSubmitEssay = useCallback(async () => {
        const isImageMode = uploadMode === "image";

        if (isImageMode && !imageState.selectedImages.length) {
            showError("請先上傳作文圖片再提交");
            return;
        } else if (isImageMode && !textState.selectedTopic) {
            showError("請先選擇題目再提交");
            return;
        } else if (!isImageMode && !textState.essayText.trim()) {
            showError("請先輸入作文內容再提交");
            return;
        } else if (!textState.selectedTopic) {
            showError("請先選擇題目再提交");
            return;
        }

        // 顯示確認彈窗
        const selectedChapter = chapterOptions.find(ch => ch._id === textState.selectedTopic);
        const chapterName = selectedChapter?.chapter || '';

        const confirmResult = await Swal.fire({
            title: "交卷前請確認",
            html: `<div style="display:flex; justify-content:center; width:100%">
        <div style="display:flex; flex-direction:column; gap:8px; align-items:start">
          <span>書籍：${bookName || ''}</span>
          <span>題目：${chapterName}</span>
          <span>是否確認交卷？</span>
        </div>
      </div>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#ffffff",
            cancelButtonText: "取消",
            confirmButtonText: "確認",
            reverseButtons: true,
            customClass: {
                cancelButton: 'custom-cancel-button'
            },
            didOpen: () => {
                const cancelBtn = document.querySelector('.custom-cancel-button') as HTMLElement;
                if (cancelBtn) {
                    cancelBtn.style.color = '#3085d6';
                    cancelBtn.style.border = '1px solid #3085d6';
                }
            }
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        trackEvent("essay_submit_started", {
            upload_mode: uploadMode,
            ...(isImageMode
                ? {
                    image_count: imageState.selectedImages.length,
                    has_topic: Boolean(textState.selectedTopic),
                    topic_id: textState.selectedTopic || "none",
                }
                : {
                    text_length: textState.essayText.length,
                    has_topic: Boolean(textState.selectedTopic),
                    topic_id: textState.selectedTopic || "none",
                }),
        });

        setIsSubmitting(true);
        resetApiState();
        hideError();

        // 送出失敗的錯誤紀錄(kanban 1003072)用的身分、題目與頁面,在導頁「之前」取好:
        // 背景 catch 執行時網址已經是 /student-home
        const submitErrorContext = getClientErrorContext();

        try {
            // ── 交卷前的圖片處理提前到導頁「之前」(kanban 113663 QA 追修)──
            // 烤旋轉/AI 優化原本跑在 navigate 之後的背景 setTimeout:
            // 低階機上 canvas 重編碼與模型推論塞滿主執行緒,新頁面畫不出來,
            // 學生看到的是白畫面、沒有任何提示。改成先把圖處理完(期間全螢幕顯示
            // 「圖片處理中請稍候」,樣式沿用 BatchUpload 交卷前的 prepareProgress 橫幅),
            // 處理完才導頁;網路上傳(uploadImageToDify)仍留在導頁後的背景執行,
            // 學生不必等上傳完成。附帶效果:這段真的 throw 時會落到外層 catch、
            // 留在本頁顯示錯誤可重試,而不是導頁後無聲失敗、首頁「批改中」永遠等不到結果。
            let imagesToUpload: File[] = [];
            // 這次送的 imagesToUpload 是否全部是前端已校正＋分段的細條(kanban 1003418)。
            // 只有下面的 croppedBlobs 分支會設 true,送件時帶 clientCorrected=1,後端
            // (kanban 1003420)就不再對細條做四角校正;整頁送出(skipCorrect、autoOptimize)
            // 一律不帶,後端照舊校正。語意是「這批檔案校正過了」,不是「目前是舊動線」。
            let clientCorrected = false;
            if (isImageMode) {
                setIsPreparingImages(true);
                try {
                    const skipCorrect = isSkipCorrectExperiment();

                    // ── 交卷這一刻把預覽區的旋轉角度烤進圖檔像素(kanban 113663) ──
                    // 之前 imageRotations 只被 CSS transform 讀，送出的還是原始 bytes，
                    // 老師把稿紙轉正、交卷，AI 拿到的還是歪的。
                    //
                    // 三條分支的判斷：
                    //  1. skipCorrect（原圖直送）→ 要烤。這條完全跳過校正視窗，
                    //     預覽區的旋轉鈕是老師唯一能轉正的地方。
                    //  2. croppedBlobs（校正+分割後的圖）→ 要烤。校正輸出本身已轉正、
                    //     且 handleCropComplete 會把該 group 角度歸零（避免轉兩次），
                    //     所以正常情況這裡角度是 0、直接沿用原檔；
                    //     原本老師在校正完成「之後」還能在預覽區再轉，所以這條也接了烤角度；
                    //     kanban 1003418 起已分段的圖不再顯示縮圖旋轉鈕（角度會逐條各自烤、
                    //     不是整頁轉正），這裡的角度正常一律是 0，保留烤角度只當防守。
                    //     ⚠️ 這條的攤平規則跟 1/3 不同（只收 isProcessed 的 group、一 group 多檔），
                    //     所以走 collectProcessedFilesWithRotation 自己展開，不共用 collectSubmitFiles。
                    //  3. else（autoOptimizeImage）→ 要烤，且先烤再優化：
                    //     優化是逐像素的銳化/對比，與方向無關，先轉正語意較單純，
                    //     而且 index 對應是直接算在 selectedImages 上、不經第二層轉手。
                    //
                    // 角度 0 一律沿用原檔、不重新編碼，避免無謂畫質損失。

                    // 分支 1 / 3 專用：送出清單直接從 processedGroups 展開
                    // （originalFile + 同 group 的角度）。之前吃 selectedImages 再按 index
                    // 配角度 ——「重設圖片」只從 processedGroups 拿掉一格、selectedImages
                    // 要到歸零才清，刪 1 張後長度守門一觸發整批不轉，而已刪掉的那張
                    // 還照樣被送出去。從 groups 展開後同 key 天生同步，被刪的自然消失，
                    // 也與 originFiles（collectOriginSourcesWithRotation）同一套展開規則，
                    // 同一個 group 兩邊拿到的方向不會分岔。
                    const collectSubmitFiles = async (): Promise<File[]> => {
                        const entries = collectSubmitOriginalsWithRotation(
                            imageState.processedGroups,
                            imageState.imageRotations
                        );
                        if (entries.length === 0) {
                            // 理論上不可達（selectedImages 非空時 processedGroups 必有同批 group）。
                            // 真的發生時 fail-open 沿用舊清單，寧可不轉也不要交不出卷。
                            console.warn("[rotation] processedGroups 為空，改送 selectedImages 原清單");
                            return imageState.selectedImages;
                        }
                        const baked: File[] = [];
                        // 逐張 await：多張大圖同時 decode + 重編碼會吃爆手機記憶體
                        for (const { file, angle } of entries) {
                            baked.push(
                                angle === 0 ? file : await bakeRotationIntoImage(file, angle)
                            );
                        }
                        return baked;
                    };

                    if (skipCorrect) {
                        // DEV 實驗：跳過所有處理，原圖直送
                        console.log("🧪 [skipCorrect] 跳過校正/分割/AI 優化，直接送原圖");
                        imagesToUpload = await collectSubmitFiles();
                    } else if (imageState.croppedBlobs.length > 0) {
                        // 這條送出的一律是分割編輯器切好的細條(含下面 entries 為空的 fallback,
                        // 那也是 croppedBlobs 本身),都在調整邊界視窗校正過 → 帶標記
                        clientCorrected = true;
                        // 如果有分割後的圖片，使用分割後的圖片。
                        // 檔案與角度「同一趟」從 processedGroups 展開（跟 handleCropComplete
                        // 產生 croppedBlobs 的那趟完全一致），index 天生對齊，
                        // 不再從 croppedBlobs 依 index 回推角度 —— 之前那樣兩邊攤平規則不同，
                        // 兩頁 PDF 只切了一頁、或按過「重設圖片」都會長度對不上而整批不轉。
                        const entries = collectProcessedFilesWithRotation(
                            imageState.processedGroups,
                            imageState.imageRotations
                        );
                        if (entries.length === 0) {
                            // 理論上不可達（沒有 isProcessed 的 group 就不會有 croppedBlobs）。
                            // 真的發生時 fail-open 沿用舊路徑，寧可不轉也不要交不出卷。
                            console.warn("[rotation] 有 croppedBlobs 但沒有已處理的 group，改送原 blobs");
                            imagesToUpload = imageState.croppedBlobs.map((blob, index) =>
                                new File([blob], `cropped-image-${index + 1}.jpg`, { type: 'image/jpeg' })
                            );
                        } else {
                            const baked: File[] = [];
                            // 逐張 await：多張大圖同時 decode + 重編碼會吃爆手機記憶體
                            for (let i = 0; i < entries.length; i++) {
                                // 檔名沿用原本的格式，不動送出的 wire format
                                const named = new File([entries[i].file], `cropped-image-${i + 1}.jpg`, { type: 'image/jpeg' });
                                baked.push(
                                    entries[i].angle === 0
                                        ? named
                                        : await bakeRotationIntoImage(named, entries[i].angle)
                                );
                            }
                            imagesToUpload = baked;
                        }
                    } else {
                        // 順序：烤旋轉 → AI 圖片優化。
                        // 這條是整頁送出（沒有前端切好的細條），四角校正交給批改後端管線
                        // （kanban 114186），送件不帶 clientCorrected；調整邊界視窗打開時的
                        // /cornerdetect 自動預填是另一回事（見 initializePerspectivePoints）。
                        // 後續的優化迴圈吃這份 groups 展開的清單，不再碰可能過期的 selectedImages。
                        const rotatedImages = await collectSubmitFiles();

                        const optimizedImages: File[] = [];

                        for (const image of rotatedImages) {
                            try {
                                const optimizedImage = await autoOptimizeImage(image);
                                optimizedImages.push(optimizedImage);
                            } catch (error) {
                                console.error('圖片優化失敗，使用原圖:', error);
                                optimizedImages.push(image);
                            }
                        }
                        imagesToUpload = optimizedImages;
                    }
                } finally {
                    // 成功失敗都要收掉提示;失敗會落到外層 catch 顯示錯誤、留在本頁可重試
                    setIsPreparingImages(false);
                }
            }

            // 保存提交資料以供後續處理
            const submissionData = {
                isImageMode,
                selectedImages: isImageMode ? imageState.selectedImages : null,
                essayText: isImageMode ? "" : textState.essayText,
                selectedTopic: textState.selectedTopic,
                bookId,
                bookName,
                chapterOptions,
                uploadMode
            };

            sessionStorage.setItem('pendingSubmission', JSON.stringify(submissionData));

            trackEvent("essay_submit_initiated", {
                upload_mode: uploadMode,
                immediate_redirect: true,
            });

            // 導回首頁，攜帶班級資訊以便自動選中正確的班級(圖片已備妥,不會再卡主執行緒)
            navigate(`/student-home?classCode=${classCode}&bookId=${encodeURIComponent(bookId || "")}&fromUpload=true`);

            // 立即通知 StudentHome 有新的提交（用於觸發批改狀態查詢）
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('recordStatusUpdate'));
            }, 100);

            // 在背景處理 AI 批改(圖片已在導頁前備妥,這裡只剩上傳與批改流程)
            setTimeout(async () => {
                try {
                    let result: unknown;

                    if (isImageMode) {
                        result = await uploadImageToDify(
                            imagesToUpload,
                            imageState.processedGroups,
                            imageState.imageRotations,
                            clientCorrected
                        );

                        // 保存圖片模式的題目資訊
                        if (textState.selectedTopic) {
                            const selectedChapter = chapterOptions.find(ch => ch._id === textState.selectedTopic);
                            if (selectedChapter) {
                                sessionStorage.setItem("topicData", JSON.stringify(selectedChapter));
                                sessionStorage.setItem("essayUnit", selectedChapter.chapter);
                            }
                        }
                    } else {
                        result = await runWorkflow({
                            text: textState.essayText,
                            unitId: textState.unitId,
                            topicId: textState.selectedTopic,
                        });

                        sessionStorage.setItem("essayText", textState.essayText);
                        sessionStorage.setItem("essayTitle", textState.essayTitle);
                        sessionStorage.setItem("essayUnit", textState.unitId);

                        if (textState.selectedTopic) {
                            const selectedChapter = chapterOptions.find(ch => ch._id === textState.selectedTopic);
                            if (selectedChapter) {
                                sessionStorage.setItem("topicData", JSON.stringify(selectedChapter));
                            }
                        }
                    }

                    // 標記最新完成的記錄ID（只保存一個）
                    try {
                        if (result && typeof result === 'object' && '_id' in result) {
                            localStorage.setItem('latestCompletedRecord', (result as any)._id);
                        }
                    } catch {
                        // 忽略錯誤
                    }

                    // 保存完整結果供後續查看
                    sessionStorage.setItem("workflowResult", JSON.stringify(result));
                    sessionStorage.setItem("uploadMode", uploadMode);

                    trackEvent("essay_submit_success", {
                        upload_mode: uploadMode,
                        result_received: true
                    });

                    // 觸發 StudentHome 重新載入（如果用戶還在該頁面）
                    window.dispatchEvent(new CustomEvent('recordStatusUpdate'));

                } catch (error: unknown) {
                    console.error("❌ 背景處理批改失敗:", error);
                    const errorMessage = error instanceof Error ? error.message : String(error);
                    // 人已經在列表頁，這裡沒有畫面可顯示：把原因暫存交給列表頁跳出（kanban 1000873）
                    stashSubmitError(extractSubmitErrorMessage(error));
                    
                    trackEvent("essay_submit_failed", {
                        upload_mode: uploadMode,
                        error_message: errorMessage
                    });

                    // 觸發 StudentHome 重新載入
                    window.dispatchEvent(new CustomEvent('recordStatusUpdate'));

                    // 錯誤紀錄(kanban 1003072):排在通知列表之後,不 await、失敗吞掉。
                    // 只在這裡記一次,uploadImageToDify / runWorkflow 自己的 catch 不再記
                    void reportClientError(
                        sendClientErrorLog,
                        submitErrorContext,
                        submitFailedError(
                            "submit_failed_background",
                            extractSubmitErrorMessage(error),
                            error,
                            isImageMode
                                ? imageSubmitStats(imagesToUpload)
                                : { textLength: textState.essayText.length }
                        )
                    );
                }
            }, 100); // 短暫延遲確保導航完成
        } catch (error: unknown) {
            console.error("提交作文失敗", error);

            // 與背景送出失敗共用同一份文案對照（kanban 1000873）
            const errorMessage = extractSubmitErrorMessage(error);

            trackEvent("essay_submit_failed", {
                upload_mode: uploadMode,
                error_message: errorMessage,
                original_error: error instanceof Error ? error.message : String(error),
            });

            showError(errorMessage);

            // 錯誤紀錄(kanban 1003072):不 await、失敗吞掉
            void reportClientError(
                sendClientErrorLog,
                submitErrorContext,
                submitFailedError(
                    "submit_failed_before_navigate",
                    errorMessage,
                    error,
                    isImageMode
                        ? imageSubmitStats(imageState.processedGroups.map((group) => group?.originalFile))
                        : { textLength: textState.essayText.length }
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        uploadMode,
        imageState.selectedImages,
        imageState.croppedBlobs,
        imageState.processedGroups,
        imageState.imageRotations,
        textState,
        showError,
        resetApiState,
        hideError,
        uploadImageToDify,
        runWorkflow,
        setApiState,
        navigate,
        trackEvent,
        chapterOptions,
        autoOptimizeImage,
        bookName,
        bookId,
        getClientErrorContext,
    ]);

    // 載入中顯示
    if (loading) {
        return <LoadingScreen message="資料載入中..." />;
    }

    // 未登入
    if (!isAuthenticated) {
        return <LoadingScreen message="尚未登入或權限不足" error={true} />;
    }

    // 主要內容渲染
    const skipCorrectActive = isSkipCorrectExperiment();

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Header />

            {skipCorrectActive && (
                <div className="bg-yellow-100 border-b border-yellow-400 text-yellow-900 text-sm px-3 py-2 text-center">
                    🧪 DEV 實驗模式：<strong>skipCorrect=1</strong> — 已跳過透視校正、圖片分割、AI 自動優化，圖片將以原始壓縮版直送批改
                </div>
            )}

            <main className="flex-grow container mx-auto px-2 py-4">
                {/* 麵包屑導航 */}
                <div className="flex items-center py-4">
                  <BreadCrumb breadCrumb="/student/home/upload" />
                </div>
                <hr className="text-black/20 mb-4" />

                {/* Mode Toggle */}
                <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 pb-2">
                        上傳方式
                    </h2>

                    <UploadModeToggle
                        currentMode={uploadMode}
                        onToggle={toggleUploadMode}
                        isSubmitting={isSubmitting}
                    />

                    {/* 依據上傳模式顯示不同內容 */}
                    {uploadMode === "image" ? (
                        <ImageUploadSection
                            previewUrls={imageState.previewUrls}
                            selectedImages={imageState.selectedImages}
                            imageError={imageState.imageError}
                            isSubmitting={isSubmitting}
                            isProcessing={isProcessing}
                            handleImageChange={handleImageChange}
                            removeProcessedGroup={removeProcessedGroup}
                            selectedTopic={textState.selectedTopic}
                            handleSelectTopic={handleSelectTopic}
                            chapterOptions={chapterOptions}
                            showPerspectiveEditor={imageState.showPerspectiveEditor}
                            currentEditingImageIndex={imageState.currentEditingImageIndex}
                            perspectivePointsArray={imageState.perspectivePointsArray}
                            processedImageUrlArray={imageState.processedImageUrlArray}
                            setPerspectivePoints={setPerspectivePoints}
                            setProcessedImageUrl={setProcessedImageUrl}
                            applyProcessedImage={applyProcessedImage}
                            draggedCorner={draggedCorner}
                            setDraggedCorner={setDraggedCorner}
                            dragOffset={dragOffset}
                            setDragOffset={setDragOffset}
                            needsInitialization={imageState.needsInitialization}
                            clearInitializationFlag={clearInitializationFlag}
                            closePerspectiveEditor={closePerspectiveEditor}
                            onEnterCropMode={() => {
                                // 先關閉透視校正編輯器，再開啟分割編輯器
                                setImageState(prev => {
                                    const newManuallyAdjusted = [...prev.perspectiveManuallyAdjusted];
                                    // 標記當前圖片為手動調整過透視校正
                                    newManuallyAdjusted[prev.currentEditingImageIndex] = true;
                                    
                                    return {
                                        ...prev,
                                        showPerspectiveEditor: false,
                                        showCropEditor: true,
                                        perspectiveManuallyAdjusted: newManuallyAdjusted
                                    };
                                });
                            }}
                            originalImageCount={imageState.originalImageCount}
                            processedGroups={imageState.processedGroups}
                            imageRotations={imageState.imageRotations}
                            rotateGroupImage={rotateGroupImage}
                            currentProcessingGroupId={imageState.currentProcessingGroupId}
                            confirmRotation={confirmRotation}
                            confirmRotationStep={confirmRotationStep}
                            getClientErrorContext={getClientErrorContext}
                        />
                    ) : (
                        <TextUploadSection
                            essayText={textState.essayText}
                            selectedTopic={textState.selectedTopic}
                            textError={textState.textError}
                            isSubmitting={isSubmitting}
                            isProcessing={isProcessing}
                            handleTextChange={handleTextChange}
                            handleSelectTopic={handleSelectTopic}
                            chapterOptions={chapterOptions}
                        // isLoadingChapters={isLoadingChapters} // 不再使用
                        />
                    )}
                </div>

                {/* Processing Status Message */}
                {isProcessing && (
                    <div className="flex justify-center mb-4">
                        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded-md max-w-md text-center">
                            <div className="flex items-center justify-center mb-2">
                                <span className="w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin mr-2"></span>
                                <span className="font-medium">評改進行中</span>
                            </div>
                            <p className="text-sm">{processingMessage}</p>
                        </div>
                    </div>
                )}

                {/* Submit button */}
                <div className="flex justify-center mb-6">
                    <button
                        onClick={handleSubmitEssay}
                        disabled={isSubmitDisabled}
                        title={isProcessing ? "評改中，請等待完成後再上傳新的作文" : ""}
                        className={`${isSubmitDisabled
                                ? "bg-gray-500 cursor-not-allowed"
                                : "bg-[#2D86FF] hover:bg-blue-600"
                            } text-white font-medium py-2 px-16 rounded-full transition-colors flex items-center`}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                                AI辨識中...
                            </>
                        ) : (
                            "交卷"
                        )}
                    </button>
                </div>

                {/* 交卷前的圖片處理提示(kanban 113663 QA 追修):烤旋轉+AI 優化在低階機上
                    要跑數秒,期間全螢幕鎖定並顯示處理中,學生才不會以為當掉或狂按其他按鈕。
                    橫幅樣式沿用 BatchUpload 交卷前的 prepareProgress;單篇最多 2 張,
                    依驗收共識顯示「圖片處理中請稍候」、不帶 (n / m) 計數。 */}
                {isPreparingImages && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="status" aria-live="polite">
                        <div className="absolute inset-0 bg-black bg-opacity-50" />
                        <div className="relative p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3 shadow-lg">
                            <span className="w-5 h-5 border-2 border-[#2D86FF] border-t-transparent rounded-full animate-spin"></span>
                            <span className="text-sm text-[#254D71] font-medium">圖片處理中請稍候</span>
                        </div>
                    </div>
                )}
            </main>

            {/* Error Popup */}
            {errorPopup.show && (
                <ErrorPopup message={errorPopup.message} onClose={hideError} />
            )}

            {/* 分割編輯器 */}
            {imageState.showCropEditor && imageState.processedImageUrlArray.length > 0 && (
                <PerspectiveCorrectionWithCrop
                    imageUrl={imageState.processedImageUrlArray[imageState.currentEditingImageIndex] || imageState.previewUrls[imageState.currentEditingImageIndex]}
                    // 綁定這次開啟時的 group:非同步回呼晚到時,換了張就不寫回(kanban 1003418)
                    onConfirm={(images) => handleCropComplete(images, imageState.currentProcessingGroupId)}
                    onCancel={closeCropEditor}
                    onPrevious={goBackToPerspectiveEditor}
                />
            )}

            <Footer />
        </div>
    );
}

// 載入中畫面組件
function LoadingScreen({ message = "資料載入中...", error = false }) {
    const colorClass = error ? "text-red-600" : "text-blue-800";
    const borderClass = error ? "border-red-600" : "border-blue-800";

    return (
        <div className="flex h-screen items-center justify-center bg-gray-50">
            <div className="text-center">
                <h1 className={`text-2xl font-bold ${colorClass} mb-4`}>{message}</h1>
                {error && <p className="text-gray-700 mb-4">即將回到首頁...</p>}
                <div
                    className={`w-12 h-12 border-4 ${borderClass} border-t-transparent rounded-full animate-spin mx-auto`}
                ></div>
            </div>
        </div>
    );
}

// 上傳模式切換組件
function UploadModeToggle({
    currentMode,
    onToggle,
    isSubmitting
}: {
    currentMode: "image" | "text";
    onToggle: (mode: "image" | "text") => void;
    isSubmitting: boolean;
}) {
    const { trackEvent } = useGAEventTracker();

    const handleToggle = (mode: "image" | "text") => {
        if (currentMode !== mode) {
            trackEvent("upload_toggle_clicked", {
                from_mode: currentMode,
                to_mode: mode,
            });
            onToggle(mode);
        }
    };

    return (
        <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-md shadow-sm w-full max-w-md" role="group">
                <ModeButton
                    mode="image"
                    currentMode={currentMode}
                    onToggle={handleToggle}
                    isSubmitting={isSubmitting}
                    label="圖片上傳"
                    className="rounded-l-lg"
                />
                <ModeButton
                    mode="text"
                    currentMode={currentMode}
                    onToggle={handleToggle}
                    isSubmitting={isSubmitting}
                    label="文字輸入"
                    className="rounded-r-lg"
                />
            </div>
        </div>
    );
}

// 模式按鈕組件
function ModeButton({
    mode,
    currentMode,
    onToggle,
    isSubmitting,
    label,
    className = "",
}: {
    mode: "image" | "text";
    currentMode: "image" | "text";
    onToggle: (mode: "image" | "text") => void;
    isSubmitting: boolean;
    label: string;
    className?: string;
}) {
    return (
        <button
            type="button"
            onClick={() => onToggle(mode)}
            className={`flex-1 px-6 py-3 text-sm font-medium border border-gray-200 ${currentMode === mode
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                } ${className}`}
            disabled={isSubmitting}
        >
            <div className="flex flex-col items-center">
                {mode === "image" ? (
                    <svg
                        className="w-6 h-6 mb-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                ) : (
                    <svg
                        className="w-6 h-6 mb-1"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                    </svg>
                )}
                {label}
            </div>
        </button>
    );
}

/**
 * 上傳頁的預覽縮圖:用 canvas 畫「已套用旋轉」的畫面(kanban 113663 QA 追修)。
 *
 * 原本是 <img> + CSS transform rotate:transform 不改變 layout box,
 * 90/270 度時視覺高度超出容器,溢出的部分會蓋住下方的「重設圖片」按鈕
 * (DOM 還在但點不到)。buildRotatedSource 在 90/270 時已互換 canvas 寬高,
 * 瀏覽器以互換後的尺寸重新 fit 進同一個容器,四個角度都完整顯示、不溢出。
 * 與旋轉視窗、BatchUpload 的 RotatedFacePreview 同一套解法(含 cancelled flag)。
 *
 * 載入失敗一律留白:縮圖畫不出來不該擋住「重設圖片」;真正的壞檔在上傳入圖階段
 * (handleImageChange 的 isFileDecodable)就會被驗掉,漏網的在旋轉視窗開啟時
 * 也會被攔下並移除(見 ImageUploadSection 的 handleRotateImageLoadFailure)。
 */
function RotatedThumbnail({
    src,
    angle,
    label,
    className,
}: {
    src: string;
    angle: number;
    label: string;
    className: string;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        // 換圖/換角度時,舊的 img.onload 不可再往(可能已被重用的)canvas 上畫
        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            const canvas = canvasRef.current;
            if (cancelled || !canvas) return;
            // 每次都從原圖重算(而非在上一次結果上再轉):連按 4 次能精確回正、畫質不累積損失
            const source = buildRotatedSource(img, angle);
            // canvas 沒有 naturalWidth/naturalHeight,退回 width/height
            const width = ("naturalWidth" in source ? source.naturalWidth : 0) || source.width;
            const height = ("naturalHeight" in source ? source.naturalHeight : 0) || source.height;
            if (!width || !height) return;
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            ctx.drawImage(source, 0, 0);
        };
        img.src = src;
        return () => {
            cancelled = true;
        };
    }, [src, angle]);

    // canvas 沒有 alt,用 role+aria-label 補上與原本 <img alt> 等價的資訊
    return <canvas ref={canvasRef} role="img" aria-label={label} className={className} />;
}

// 圖片上傳區域元件
function ImageUploadSection({
    previewUrls,
    selectedImages,
    imageError,
    isSubmitting,
    isProcessing,
    handleImageChange,
    removeProcessedGroup,
    selectedTopic,
    handleSelectTopic,
    chapterOptions,
    showPerspectiveEditor,
    currentEditingImageIndex,
    perspectivePointsArray,
    processedImageUrlArray,
    setPerspectivePoints,
    setProcessedImageUrl,
    applyProcessedImage,
    draggedCorner,
    setDraggedCorner,
    dragOffset,
    setDragOffset,
    needsInitialization,
    clearInitializationFlag,
    closePerspectiveEditor,
    onEnterCropMode,
    originalImageCount,
    processedGroups,
    imageRotations,
    rotateGroupImage,
    currentProcessingGroupId,
    confirmRotation,
    confirmRotationStep,
    getClientErrorContext,
}: {
    previewUrls: string[];
    selectedImages: File[];
    imageError: string | null;
    isSubmitting: boolean;
    isProcessing: boolean;
    originalImageCount: number;
    processedGroups: ProcessedImageGroup[];
    handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    removeProcessedGroup: (groupIndex: number) => void;
    selectedTopic: string;
    handleSelectTopic: (chapterId: string, chapterName: string) => void;
    chapterOptions: Chapter[];
    showPerspectiveEditor: boolean;
    currentEditingImageIndex: number;
    perspectivePointsArray: (PerspectivePoints | null)[];
    processedImageUrlArray: (string | null)[];
    setPerspectivePoints: (points: PerspectivePoints | null) => void;
    setProcessedImageUrl: (url: string | null) => void;
    applyProcessedImage: (processedUrl: string, groupId: string | null, cornerLabel?: CornerLabelDraft | null) => void;
    draggedCorner: keyof PerspectivePoints | null;
    setDraggedCorner: (corner: keyof PerspectivePoints | null) => void;
    dragOffset: { x: number; y: number } | null;
    setDragOffset: (offset: { x: number; y: number } | null) => void;
    needsInitialization: boolean;
    clearInitializationFlag: () => void;
    closePerspectiveEditor: () => void;
    onEnterCropMode: () => void;
    imageRotations: Record<string, number>;
    rotateGroupImage: (groupId: string) => void;
    currentProcessingGroupId: string | null;
    confirmRotation: () => void;
    confirmRotationStep: () => void;
    // 前端錯誤紀錄的身分、題目與頁面(kanban 1003072),由頁面提供
    getClientErrorContext: () => ClientErrorLogContext;
}) {
    const { trackEvent } = useGAEventTracker();
    // 自動框選(打後端 /cornerdetect 預填角點;kanban 1000655 起全環境開啟,kanban 1003418 起
    // 隨調整邊界視窗在正式站生效):偵測中遮罩+run token(切圖作廢舊結果)+使用者已拖角則不覆寫
    const [isDetectingCorners, setIsDetectingCorners] = useState(false);
    const cornerDetectRunRef = useRef(0);
    const userAdjustedCornersRef = useRef(false);
    // 四角標註(kanban 1003468):調整邊界視窗這一輪的初始角點與自動框選結果。
    // 初始化(開視窗、旋轉、重設角點)開新的一輪;偵測結果只認同一輪(runToken)的;
    // 換張 / 開關視窗清掉。按「下一步」時與當下角點組成一頁標註。
    const cornerSessionRef = useRef<CornerSession | null>(null);
    // 角點 commit 後自動生成校正預覽(自動框選成功 / 旋轉後都會用)
    const pendingAutoPreviewRef = useRef(false);
    // 預覽區旋轉角度已提升到 ImageState(kanban 113663),這裡只負責觸發與顯示
    const rotateImage = (groupId: string) => {
        rotateGroupImage(groupId);
        trackEvent("rotate_image_click", { group_id: groupId });
    };
    // 四角校正視窗內的累計旋轉角度(0/90/180/270)。
    // 這個角度會烘進校正輸出的實際像素(主 canvas 與右側預覽都從原圖 + 累計角度重算),
    // 不是只轉 CSS;切換編輯的圖片 / 開關視窗時歸零。
    //
    // 只用 ref、不用 state:三個繪圖函式都在 img.onload 這種非同步 callback 裡讀角度,
    // state 會拿到舊 closure(連點兩下可能算出同一個 next),而畫面本身是命令式畫 canvas、
    // 不需要靠 state 觸發 re-render。
    const correctionRotationRef = useRef(0);
    // 旋轉 in-flight 閘門:新畫布/角點重建完之前擋掉重複點擊。
    // 否則連點會有兩個 img.onload 競賽,canvas 尺寸與 ref 角度對不上 → 使用者看到「框歪掉」。
    const rotatingRef = useRef(false);
    const perspectiveCanvasRef = useRef<HTMLCanvasElement>(null);
    const perspectivePreviewRef = useRef<HTMLCanvasElement>(null);
    const magnifierCanvasRef = useRef<HTMLCanvasElement>(null);
    // 放大鏡取樣來源:旋轉後是離屏 canvas,所以型別要能容納兩種
    const magnifierSourceImageRef = useRef<CorrectionSource | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── 旋轉視窗(kanban 113663)──
    // flag 開啟 → 渲染單欄的「調整圖片方向」視窗;flag 關閉 → 走下面既有的四角拖曳視窗,
    // 行為與現在完全一致(舊程式碼刻意保留不刪,回退成本 = 改一個 flag)。
    const rotateOnlyFlow = isRotateOnlyFlowEnabled();
    // rotatingRef 是 ref、不觸發重繪,所以另開 state 驅動兩顆按鈕的 disabled 與處理中 chip
    const [isRotating, setIsRotating] = useState(false);
    // 畫面上刻意不做角度文字指示器,螢幕閱讀器只剩這條 aria-live 管道
    const [rotationAnnouncement, setRotationAnnouncement] = useState('');
    const rotateCanvasRef = useRef<HTMLCanvasElement>(null);
    const confirmRotationButtonRef = useRef<HTMLButtonElement>(null);

    // ── 校正視窗的兩層快取(對應國中 chaiwriting-j PerspectiveEditor 的做法)──
    // 拖曳角點時每個 pointermove 都重繪一次,原本每幀 new Image() 重新解碼、
    // 旋轉狀態下還每幀多配置一個離屏 canvas(1280x960 約 4.9MB backing store)。
    // 更要命的是非同步 img.onload 會讓 canvas 短暫空白,角點「閃一下就消失」——
    // 國中已經踩過這個 bug。命中快取就同步繪製,不進 onload。
    const loadedImageRef = useRef<{ url: string; img: HTMLImageElement } | null>(null);
    const rotatedSourceRef = useRef<{ key: string; source: CorrectionSource } | null>(null);

    // 取得(必要時才建立)旋轉後的繪製來源;同一個 url + 角度只算一次。
    const getRotatedSource = useCallback((url: string, img: HTMLImageElement, angle: number): CorrectionSource => {
        const norm = ((Math.round(angle) % 360) + 360) % 360;
        const key = `${url}|${norm}`;
        const cached = rotatedSourceRef.current;
        if (cached && cached.key === key) return cached.source;
        const source = buildRotatedSource(img, norm);
        rotatedSourceRef.current = { key, source };
        return source;
    }, []);

    // 取得校正視窗要用的原圖:已解碼過就「同步」回呼,沒有才走 img.onload。
    // fallbackFile:blob URL 可能已被 revoke,用原始檔重建一次再試。
    // onload 後還會過一關 decode() 主動驗證(見 img.onload 內註解):這裡是第二道防線,
    // 壞檔正常會在上傳入圖階段(handleImageChange 的 isFileDecodable)就被驗掉。
    const withCorrectionImage = useCallback((
        url: string,
        fallbackFile: File | undefined,
        onReady: (img: HTMLImageElement) => void,
        onFail?: () => void
    ) => {
        const cached = loadedImageRef.current;
        if (cached && cached.url === url && cached.img.complete && cached.img.naturalWidth > 0) {
            onReady(cached.img);
            return;
        }
        const img = new Image();
        let retried = false;
        // fallback 重試時自建的 objectURL 要自己收:decode 完成(onload)或最終失敗
        // (onerror)就 revoke,否則每次重試都洩漏一份圖的記憶體。
        // 傳入的 url 是預覽清單持有的,生命週期不歸這裡管,不動它。
        let fallbackUrl: string | null = null;
        const revokeFallbackUrl = () => {
            if (!fallbackUrl) return;
            URL.revokeObjectURL(fallbackUrl);
            fallbackUrl = null;
        };
        // onerror 與 decode 驗證失敗共用同一條失敗路徑:先用原始檔重試一次,
        // 重試也失敗才回報 onFail(呼叫端接 handleRotateImageLoadFailure:關窗+移除+提示)
        const failOrRetry = (reason: unknown) => {
            if (!retried && fallbackFile) {
                retried = true;
                fallbackUrl = URL.createObjectURL(fallbackFile);
                img.src = fallbackUrl;
                return;
            }
            revokeFallbackUrl();
            console.error('校正視窗圖片載入失敗:', reason);
            onFail?.();
        };
        img.onload = () => {
            // onload 不代表能畫:Chrome 對「檔頭完整、內容截斷」的壞檔照樣觸發 onload
            // (naturalWidth 是檔頭寫的完整尺寸),onerror 永遠不會發生。這裡的 decode()
            // 驗證只攔得住「完全無法解析的垃圾檔」;截斷檔連 decode() 都會 resolve
            // (QA 站實測),靠上傳入圖階段 isFileDecodable 的位元組結構檢查擋下,
            // 正常不會走到這裡。verifyImageDecodable 是 fail-open:沒有 decode(舊瀏覽器)
            // /同步炸掉/逾時一律當通過、維持現狀,只有明確 reject 才判壞檔。
            verifyImageDecodable(img, IMAGE_DECODE_CHECK_TIMEOUT_MS).then((ok) => {
                if (!ok) {
                    failOrRetry(new Error('圖片 decode() 失敗(檔案內容損壞或截斷)'));
                    return;
                }
                // 已確認可解碼,之後快取都直接拿 img 重繪、不會再讀 src,revoke 是安全的
                revokeFallbackUrl();
                loadedImageRef.current = { url, img };
                onReady(img);
            });
        };
        img.onerror = (e) => failOrRetry(e);
        img.src = url;
    }, []);

    // ── 旋轉視窗的邏輯(kanban 113663);flag 關閉時整段都是死碼,不影響既有流程 ──

    // 旋轉角度的單一真值來源就是 imageRotations[group.id](與上傳頁縮圖的圓形旋轉鈕共用)。
    // currentProcessingGroupId 是視窗正在處理的那一組;理論上開著視窗它就不會是 null,
    // 真的是 null 時退回用索引找(此動線沒有分割,previewUrls 與 processedGroups 一對一),
    // 免得旋轉鈕變成按了沒反應的死鈕。
    const rotationGroupId = currentProcessingGroupId ?? processedGroups[currentEditingImageIndex]?.id ?? null;
    const rotationAngle = rotationGroupId ? (imageRotations[rotationGroupId] || 0) : 0;
    // 標題的「第 N 張 / 共 M 張」從 processedGroups + rotationGroupId 推導,
    // 不能吃 previewUrls.length / currentEditingImageIndex:「重設圖片」後那兩個是 stale 的
    // (刪 1 張再上傳會顯示「第 3 張 / 共 3 張」,實際只有 2 張)。規則見 resolveImageCounter。
    const rotationCounter = resolveImageCounter(processedGroups, rotationGroupId);

    // 壞檔(無法 decode 的圖)進旋轉視窗:withCorrectionImage 連 fallback 原始檔重試
    // 都失敗,這張圖誰都畫不出來 —— 不能讓使用者對著全空白的視窗(kanban 113663 QA 追修)。
    // 關窗 + 移除該張圖 + 以 repo 既有的 Swal 錯誤樣式告知。
    // index 由 group id 反查 processedGroups:previewUrls / currentEditingImageIndex
    // 在「重設圖片」後會 stale,反查不到(理論上不會)就只關窗不誤刪別張。
    const handleRotateImageLoadFailure = useCallback(() => {
        const failedIndex = rotationGroupId
            ? processedGroups.findIndex((group) => group.id === rotationGroupId)
            : -1;
        // 錯誤紀錄要的是被移除的那個檔,先在 removeProcessedGroup 之前取好(kanban 1003072)
        const failedFile = failedIndex >= 0 ? processedGroups[failedIndex]?.originalFile : undefined;
        // 走 confirmRotationStep 而非 confirmRotation(kanban 106497):壞檔要「跳過這張」,
        // 不是關掉整批 —— 多頁 PDF 若第 1 頁是壞檔,直接關窗會讓第 2 頁從頭到尾
        // 沒進過視窗,而視窗正是這條動線唯一提醒使用者轉正的地方。
        // 佇列空了 confirmRotationStep 自己會關窗,行為與原本一致。
        confirmRotationStep();
        if (failedIndex >= 0) removeProcessedGroup(failedIndex);
        Swal.fire({
            icon: "error",
            title: "圖片無法讀取",
            text: "圖片無法讀取，請重新拍攝或選擇其他圖片",
        });
        trackEvent("rotate_dialog_image_load_failed", {
            group_id: rotationGroupId ?? "unknown",
        });
        // 錯誤紀錄(kanban 1003072):不 await、失敗吞掉
        void reportClientError(
            sendClientErrorLog,
            getClientErrorContext(),
            rotateImageLoadFailedError("圖片無法讀取，請重新拍攝或選擇其他圖片", failedFile)
        );
    }, [rotationGroupId, processedGroups, confirmRotationStep, removeProcessedGroup, trackEvent, getClientErrorContext]);

    // 旋轉視窗的「旋轉90度」:只改角度狀態,不在此烤 dataURL。
    // in-flight 閘門沿用 rotatingRef —— 連點會有兩個 img.onload 競賽,畫布尺寸與角度對不上。
    const handleRotateInDialog = useCallback(() => {
        if (rotatingRef.current) return;
        if (!rotationGroupId) return;
        rotatingRef.current = true;
        setIsRotating(true);
        setRotationAnnouncement(formatRotationAnnouncement(nextRotationAngle(rotationAngle)));
        rotateGroupImage(rotationGroupId);
        trackEvent("rotate_image_click", { group_id: rotationGroupId, source: "rotate_dialog" });
    }, [rotationGroupId, rotationAngle, rotateGroupImage, trackEvent]);

    // 依累計角度重繪視窗內的 canvas。
    // 用 canvas 而不是 <img> + CSS transform:CSS transform 不改變 layout box,
    // 橫式圖轉 90 度後視覺高度會超出容器、被 overflow-hidden 直接裁掉;
    // buildRotatedSource 在 90/270 度已互換 canvas 寬高,瀏覽器才能以互換後的比例重新 fit。
    useEffect(() => {
        if (!rotateOnlyFlow || !showPerspectiveEditor) return;

        // 換圖/換角度時,上一輪的 img.onload 可能晚到、把舊圖畫進新畫布
        // (同 BatchUpload 的 RotatedFacePreview):cleanup 設 cancelled,
        // 晚到的回呼不畫也不動閘門 —— 閘門由新一輪 effect 自己的回呼負責釋放。
        let cancelled = false;

        // 畫完(或畫不成)都要放閘門,否則兩顆按鈕會永遠 disabled
        const release = () => {
            rotatingRef.current = false;
            setIsRotating(false);
        };

        // 畫布要跟著「視窗正在處理的那一組」自己帶的原圖,不能吃 currentEditingImageIndex,
        // 也不能拿 group 的位置去索引 previewUrls / selectedImages(kanban 106497,
        // code review 抓到的迴歸):那兩份陣列在「重設圖片」後不會同步縮短,
        // 刪一張再補上傳就錯開。規則與理由見 resolveRotationSource。
        const source = resolveRotationSource(processedGroups, rotationGroupId, {
            url: previewUrls[currentEditingImageIndex],
            file: selectedImages[currentEditingImageIndex],
        });
        if (!source || !rotateCanvasRef.current) {
            release();
            return;
        }
        const url = source.url;

        withCorrectionImage(url, source.file, (img) => {
            if (cancelled) return;
            const canvas = rotateCanvasRef.current;
            const ctx = canvas?.getContext('2d');
            if (!canvas || !ctx) { release(); return; }

            const source = getRotatedSource(url, img, rotationAngle);
            // canvas 的實際尺寸跟著來源走(90/270 度時已互換);顯示尺寸交給
            // max-w-full / max-h-full / object-contain,容器尺寸一個字都不用改
            canvas.width = source.width;
            canvas.height = source.height;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(source, 0, 0);
            release();
        }, () => {
            if (cancelled) return;
            release();
            // 最終 onerror(fallback 原始檔重試也失敗)= 壞檔:關窗、移除、提示,
            // 別讓使用者對著全空白的視窗
            handleRotateImageLoadFailure();
        });

        return () => {
            cancelled = true;
        };
        // rotationGroupId / processedGroups 必須在依賴裡:逐張處理時只有它們會變,
        // 少了就換到下一張時畫布不重繪(kanban 106497)
    }, [rotateOnlyFlow, showPerspectiveEditor, previewUrls, currentEditingImageIndex, selectedImages, withCorrectionImage, getRotatedSource, rotationAngle, handleRotateImageLoadFailure, rotationGroupId, processedGroups]);

    // ESC 關閉:語意與右上角關閉鈕一致 —— 單純關窗,圖與目前角度都保留。
    // 與「確認」不同:確認會逐張前進(kanban 106497),Esc 是放棄整個佇列。
    // 不走 closePerspectiveEditor:那是舊四角視窗「放棄這張圖」的語意,會清掉上傳狀態。
    useEffect(() => {
        if (!rotateOnlyFlow || !showPerspectiveEditor) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') confirmRotation();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [rotateOnlyFlow, showPerspectiveEditor, confirmRotation]);

    // 開啟時把焦點移到確認鈕(視窗內只有兩顆互動元素,Tab 兩下即循環),並清掉上一張的播報。
    // 依 currentProcessingGroupId 觸發:多頁 PDF 逐張處理時換的是 group 而不是
    // currentEditingImageIndex,只看索引的話第二頁不會重設播報、焦點也不會回到確認鈕
    // (kanban 106497)。
    useEffect(() => {
        if (!rotateOnlyFlow || !showPerspectiveEditor) return;
        setRotationAnnouncement('');
        confirmRotationButtonRef.current?.focus();
    }, [rotateOnlyFlow, showPerspectiveEditor, currentEditingImageIndex, currentProcessingGroupId]);

    const MAGNIFIER_SIZE = 200;
    const MAGNIFIER_ZOOM = 1.15;

    // 放大鏡相關狀態
    const [showMagnifier, setShowMagnifier] = useState(false);
    const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
    const [magnifierCenter, setMagnifierCenter] = useState({ x: 0, y: 0 });
    const magnifierUpdateTimeoutRef = useRef<number | null>(null);
    const isTouchDevice = useRef(false);


    // 包裝 handleImageChange 函數以在選擇後重置 input
    const handleImageChangeWithReset = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleImageChange(e);
        // 重置 file input value 以允許重複選擇相同檔案
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        trackEvent("view_section", {
            section_name: "image_upload",
        });
    }, [trackEvent]);

    // 監控 input 狀態變化
    useEffect(() => {
    }, [selectedImages.length, isSubmitting, showPerspectiveEditor]);

    // 獲取當前編輯圖片的透視校正點
    const currentPerspectivePoints = perspectivePointsArray[currentEditingImageIndex] || null;
    const currentProcessedImageUrl = processedImageUrlArray[currentEditingImageIndex] || null;

    // 初始化透視校正點
    // rotationOverride:剛按下「旋轉90度」時直接把新角度傳進來,不必等 ref 以外的東西生效
    // onReady:畫布與預設角點都擺好後回呼(旋轉的 in-flight 閘門靠它解除)
    const initializePerspectivePoints = useCallback((
        imageIndex?: number,
        rotationOverride?: number,
        onReady?: () => void
    ) => {
        const targetIndex = imageIndex !== undefined ? imageIndex : currentEditingImageIndex;
        const targetUrl = previewUrls[targetIndex];


        if (!targetUrl || !perspectiveCanvasRef.current) {
            onReady?.();
            return;
        }

        withCorrectionImage(targetUrl, selectedImages[targetIndex], (img) => {
            const canvas = perspectiveCanvasRef.current;
            if (!canvas) { onReady?.(); return; }

            const ctx = canvas.getContext('2d');
            if (!ctx) { onReady?.(); return; }

            // 計算容器可用尺寸
            const container = canvas.parentElement;
            if (!container) { onReady?.(); return; }

            // 依累計角度從原圖重算繪製來源(0 度時就是原圖本身)
            const angle = rotationOverride !== undefined ? rotationOverride : correctionRotationRef.current;
            const source = getRotatedSource(targetUrl, img, angle);
            const sourceWidth = source.width;
            const sourceHeight = source.height;

            const containerRect = container.getBoundingClientRect();
            const maxWidth = containerRect.width - 32; // 減去 padding
            const maxHeight = containerRect.height - 32; // 減去 padding

            // 計算縮放比例以適應容器
            const scaleX = maxWidth / sourceWidth;
            const scaleY = maxHeight / sourceHeight;
            const scale = Math.min(scaleX, scaleY, 1); // 不放大，只縮小

            const displayWidth = sourceWidth * scale;
            const displayHeight = sourceHeight * scale;

            // 設置 canvas 顯示尺寸
            canvas.style.width = displayWidth + 'px';
            canvas.style.height = displayHeight + 'px';

            // 設置 canvas 實際尺寸（保持原始解析度；旋轉 90/270 度時寬高已互換）
            canvas.width = sourceWidth;
            canvas.height = sourceHeight;
            ctx.drawImage(source, 0, 0);
            magnifierSourceImageRef.current = source;

            const margin = 0.1;
            const defaultPoints: PerspectivePoints = {
                topLeft: { x: canvas.width * margin, y: canvas.height * margin },
                topRight: { x: canvas.width * (1 - margin), y: canvas.height * margin },
                bottomLeft: { x: canvas.width * margin, y: canvas.height * (1 - margin) },
                bottomRight: { x: canvas.width * (1 - margin), y: canvas.height * (1 - margin) }
            };
            setPerspectivePoints(defaultPoints);

            // 四角標註的新一輪(kanban 1003468):預設框是這一輪的初始角點,拖角紀錄歸零
            const detectionRequested = isAutoCornerDetectionEnabled();
            userAdjustedCornersRef.current = false;
            cornerSessionRef.current = startCornerSession({
                runToken: detectionRequested ? cornerDetectRunRef.current + 1 : cornerDetectRunRef.current,
                imageWidth: canvas.width,
                imageHeight: canvas.height,
                defaultPoints,
                detectionRequested,
            });

            // 自動框選手寫區(isAutoCornerDetectionEnabled,全環境開啟;kanban 114186 改由後端 API 推論)
            // 先擺好預設 10% 框,偵測成功再覆寫;失敗/偵測不到 → 維持預設框走手動拉角
            // 旋轉後同樣要跑:後端回傳的角點座標系 = 送去 API 的那張(壓縮過的)圖的
            // 座標系,不是 source(全解析度)的座標系 —— correctionSourceToApiImage
            // 回傳的 scale 就是兩者之間的換算比例,下面用 scalePointToSource 換算回
            // perspectivePoints 要的全解析度座標系(kanban 114186 code review:
            // 不能假設 scale 恆等於 1)
            if (detectionRequested) {
                const runToken = ++cornerDetectRunRef.current;
                userAdjustedCornersRef.current = false;
                setIsDetectingCorners(true);
                const startedAt = performance.now();
                // 送 API 前的縮放比例;壓縮前就失敗時維持 null(四角標註用)
                let apiScale: number | null = null;
                // 用 .then() 把可能拋錯的同步壓縮包進 promise 鏈，讓下面的 .catch() 接得住
                // (canvas context 取不到之類的例外，不該讓整個初始化流程炸掉)。
                Promise.resolve()
                    .then(() => correctionSourceToApiImage(source))
                    .then(({ dataUrl, scale }) => {
                        apiScale = scale;
                        return { dataUrl, scale };
                    })
                    .then(({ dataUrl, scale }) =>
                        // 這條路徑只讀 result.corners,不用 warpedImage(調整邊界視窗的預填路徑,
                        // kanban 1003418 起隨手動四角動線重新生效,見 featureFlags 的說明);
                        // output: "corners" 讓後端不用多做一次透視變形、
                        // 也不用多傳一張完整圖片(kanban 114186 code review 低優先項)
                        requestCornerDetect(dataUrl, { output: "corners" }).then((result) => ({ result, scale }))
                    )
                    .then(({ result, scale }) => {
                        if (runToken !== cornerDetectRunRef.current) return; // 已切到別張圖
                        // 四角標註:這一輪的模型結果(學生已拖過角時照記,applied=false)
                        const ms = Math.round(performance.now() - startedAt);
                        // 角點不是 4 點或換算後非有限數字 → 這一輪作廢、整頁不送(畫布行為不變)
                        if (result?.corners) {
                            cornerSessionRef.current = applyDetectedCorners(
                                cornerSessionRef.current,
                                runToken,
                                result.corners,
                                { apiScale: scale, ms, applied: !userAdjustedCornersRef.current }
                            );
                        } else {
                            cornerSessionRef.current = applyCornerDetectOutcome(
                                cornerSessionRef.current,
                                runToken,
                                result
                                    ? { status: "not_found", apiScale: scale, ms }
                                    : { status: "failed", apiScale: scale, ms },
                                false
                            );
                        }
                        if (result?.corners && !userAdjustedCornersRef.current) {
                            const [tl, tr, br, bl] = result.corners;
                            setPerspectivePoints({
                                topLeft: scalePointToSource(tl, scale),
                                topRight: scalePointToSource(tr, scale),
                                bottomLeft: scalePointToSource(bl, scale),
                                bottomRight: scalePointToSource(br, scale),
                            });
                            // 角點 commit 後自動生成右側校正預覽(使用者可直接按下一步)
                            pendingAutoPreviewRef.current = true;
                            trackEvent("auto_corner_detected", {
                                ms: Math.round(performance.now() - startedAt),
                                engine: "api",
                            });
                        }
                    })
                    .catch((error) => {
                        console.warn("自動框選失敗(改手動拉角):", error);
                        if (runToken === cornerDetectRunRef.current) {
                            cornerSessionRef.current = applyCornerDetectOutcome(
                                cornerSessionRef.current,
                                runToken,
                                { status: "failed", apiScale, ms: Math.round(performance.now() - startedAt) },
                                false
                            );
                        }
                    })
                    .finally(() => {
                        if (runToken === cornerDetectRunRef.current) setIsDetectingCorners(false);
                    });
            }

            // 畫布尺寸與預設角點都已就緒 → 放行下一次旋轉
            // (自動框選是非同步的,但它有自己的 run token,不需要卡住旋轉鈕)
            onReady?.();
        }, onReady);
    }, [currentEditingImageIndex, previewUrls, setPerspectivePoints, selectedImages, withCorrectionImage, getRotatedSource, trackEvent]);

    // 監聽自動初始化需求
    useEffect(() => {
        if (needsInitialization && previewUrls.length > 0) {
            const timeoutId = setTimeout(() => {
                // 初始化當前編輯圖片的透視校正點
                initializePerspectivePoints(currentEditingImageIndex);
                clearInitializationFlag();
            }, 300);
            
            return () => clearTimeout(timeoutId);
        }
    }, [needsInitialization, previewUrls.length, currentEditingImageIndex, initializePerspectivePoints, clearInitializationFlag]);

    // 換一張圖 / 開關校正視窗 → 校正視窗的累計旋轉歸零(角度不跨圖沿用)
    useEffect(() => {
        correctionRotationRef.current = 0;
        // 換圖/重開視窗時把旋轉閘門放掉,免得上一張的 in-flight 把新一張的旋轉鈕鎖死
        rotatingRef.current = false;
        // 順手丟掉上一張的解碼結果與旋轉離屏 canvas,不要讓 backing store 一直留著
        // (快取本來就以 url / url|角度 為 key,不清也不會拿錯,這裡純粹是釋放記憶體)
        loadedImageRef.current = null;
        rotatedSourceRef.current = null;
        // 上一張 / 上一次開窗的四角標註紀錄作廢,等這次初始化開新的一輪(kanban 1003468)
        cornerSessionRef.current = null;
    }, [currentEditingImageIndex, showPerspectiveEditor]);

    // 四角校正動線逐張處理時換了 group 也要歸零(kanban 1003418):兩頁 PDF 在第 1 張的
    // 調整邊界視窗按 X,會接著開第 2 張 —— 編輯索引還是 0、視窗也一直開著,上面那個 effect
    // 不會觸發,第 1 張轉過的角度會被帶到第 2 張。上一張還在途中的自動框選結果也一併作廢,
    // 免得晚到的角點套到這一張。旋轉視窗有自己的閘門與重繪流程,不經過這裡。
    useEffect(() => {
        if (rotateOnlyFlow) return;
        correctionRotationRef.current = 0;
        rotatingRef.current = false;
        loadedImageRef.current = null;
        rotatedSourceRef.current = null;
        cornerDetectRunRef.current += 1;
        setIsDetectingCorners(false);
        cornerSessionRef.current = null;
    }, [rotateOnlyFlow, currentProcessingGroupId]);

    // 「重設角點」:重新擺預設框並重跑自動框選。四角標註(kanban 1003468)的新一輪,
    // 先清掉上一輪;initializePerspectivePoints 提早 return 時寧可不記,也不要記到上一輪
    const handleResetCorners = useCallback(() => {
        cornerSessionRef.current = null;
        initializePerspectivePoints();
    }, [initializePerspectivePoints]);

    // 校正視窗「旋轉90度」:點一次順時針 90 度、可累加至 360 回正。
    // 一律從原圖 + 累計角度重算(不是在已旋轉的結果上再轉),所以連按 4 次會精確回到原圖。
    const handleRotateCorrection = useCallback(() => {
        // in-flight:上一次旋轉的畫布還沒重建完就再點,會有兩個 onload 競賽 → 框歪掉
        if (rotatingRef.current) return;
        rotatingRef.current = true;
        // 四角標註(kanban 1003468):旋轉是新的一輪,先清掉上一輪;
        // initializePerspectivePoints 提早 return 時寧可不記,也不要記到舊座標系的紀錄
        cornerSessionRef.current = null;

        // 🔴 立刻作廢舊的「校正後預覽」:此刻 currentProcessedImageUrl 還是上一次拖角點
        // 產生的「未旋轉」dataURL,而「下一步」只看它有沒有值。新預覽要等 blob 解碼 →
        // 旋轉 → 逐像素透視取樣 → toDataURL,手機上是數百毫秒到數秒;這段期間若讓使用者
        // 按下一步,送出的就是完全沒轉正的圖,而且後續 handleCropComplete 還會把角度歸零、
        // 使用者毫無補救機會(kanban 113663)。清成 null 讓按鈕 disabled 到新預覽 commit 為止。
        setProcessedImageUrl(null);

        // 以 ref 為真值來源(模組其他地方都讀 ref);讀 state 的話快速連點可能算出同一個 next
        const next = (correctionRotationRef.current + 90) % 360;
        // 自動框選的結果是舊座標系的角點,旋轉後套上去會歪掉 → 作廢在途中的偵測
        cornerDetectRunRef.current += 1;
        setIsDetectingCorners(false);
        userAdjustedCornersRef.current = false;

        correctionRotationRef.current = next;
        // 主 canvas 寬高互換 + 以新尺寸重新套用預設四角點(等同觸發一次「重設角點」);
        // 直接把 next 傳進去,不用等其他狀態生效。畫布就緒後解除旋轉閘門。
        initializePerspectivePoints(undefined, next, () => {
            rotatingRef.current = false;
        });
        // 角點是非同步 commit 的,右側預覽交給角點更新後的 effect 補跑,
        // 否則 applyPerspectiveCorrection 的 closure 會拿到舊角點 → 「下一步」帶走未轉正的輸出
        pendingAutoPreviewRef.current = true;
    }, [initializePerspectivePoints, setProcessedImageUrl]);

    // 雙線性插值
    const bilinearInterpolate = useCallback((
        tl: number, tr: number,
        bl: number, br: number,
        u: number, v: number
    ): number => {
        const top = tl * (1 - u) + tr * u;
        const bottom = bl * (1 - u) + br * u;
        return top * (1 - v) + bottom * v;
    }, []);

    // 繪製透視校正後的圖片
    const drawPerspectiveImage = useCallback((
        ctx: CanvasRenderingContext2D,
        img: CorrectionSource, // 旋轉後來源是 canvas,兩者都有 width/height 且可被 drawImage 取用
        src: PerspectivePoints,
        _dst: PerspectivePoints,
        width: number,
        height: number
    ) => {
        const imageData = ctx.createImageData(width, height);

        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d')!;
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        tempCtx.drawImage(img, 0, 0);
        const srcImageData = tempCtx.getImageData(0, 0, img.width, img.height);

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const u = x / width;
                const v = y / height;

                const srcX = bilinearInterpolate(
                    src.topLeft.x, src.topRight.x,
                    src.bottomLeft.x, src.bottomRight.x,
                    u, v
                );

                const srcY = bilinearInterpolate(
                    src.topLeft.y, src.topRight.y,
                    src.bottomLeft.y, src.bottomRight.y,
                    u, v
                );

                if (srcX >= 0 && srcX < img.width && srcY >= 0 && srcY < img.height) {
                    const srcIndex = (Math.floor(srcY) * img.width + Math.floor(srcX)) * 4;
                    const dstIndex = (y * width + x) * 4;

                    imageData.data[dstIndex] = srcImageData.data[srcIndex];
                    imageData.data[dstIndex + 1] = srcImageData.data[srcIndex + 1];
                    imageData.data[dstIndex + 2] = srcImageData.data[srcIndex + 2];
                    imageData.data[dstIndex + 3] = 255;
                }
            }
        }

        ctx.putImageData(imageData, 0, 0);
    }, [bilinearInterpolate]);

    // 應用透視校正
    const applyPerspectiveCorrection = useCallback(() => {
        const currentImageUrl = previewUrls[currentEditingImageIndex];
        if (!currentPerspectivePoints || !perspectivePreviewRef.current || !currentImageUrl) return;

        const canvas = perspectivePreviewRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const selectedRegionWidth = Math.max(
            Math.abs(currentPerspectivePoints.topRight.x - currentPerspectivePoints.topLeft.x),
            Math.abs(currentPerspectivePoints.bottomRight.x - currentPerspectivePoints.bottomLeft.x)
        );
        const selectedRegionHeight = Math.max(
            Math.abs(currentPerspectivePoints.bottomLeft.y - currentPerspectivePoints.topLeft.y),
            Math.abs(currentPerspectivePoints.bottomRight.y - currentPerspectivePoints.topRight.y)
        );

        const aspectRatio = selectedRegionWidth / selectedRegionHeight;

        const maxSize = 1200;
        let outputWidth, outputHeight;

        if (aspectRatio > 1) {
            outputWidth = Math.min(maxSize, selectedRegionWidth);
            outputHeight = outputWidth / aspectRatio;
        } else {
            outputHeight = Math.min(maxSize, selectedRegionHeight);
            outputWidth = outputHeight * aspectRatio;
        }

        canvas.width = Math.round(outputWidth);
        canvas.height = Math.round(outputHeight);

        const destPoints = {
            topLeft: { x: 0, y: 0 },
            topRight: { x: canvas.width, y: 0 },
            bottomLeft: { x: 0, y: canvas.height },
            bottomRight: { x: canvas.width, y: canvas.height }
        };

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        withCorrectionImage(currentImageUrl, selectedImages[currentEditingImageIndex], (tempImg) => {
            // 校正取樣來源同樣要套用累計旋轉角度,轉正才會真的烘進校正輸出的像素
            // (下一步 → applyProcessedImage 用的就是這張 canvas 的 dataURL)
            const source = getRotatedSource(currentImageUrl, tempImg, correctionRotationRef.current);
            drawPerspectiveImage(ctx, source, currentPerspectivePoints, destPoints, canvas.width, canvas.height);
            setProcessedImageUrl(canvas.toDataURL());
        });
    }, [currentPerspectivePoints, previewUrls, currentEditingImageIndex, setProcessedImageUrl, drawPerspectiveImage, selectedImages, withCorrectionImage, getRotatedSource]);

    // 角點 commit 後生成校正預覽(applyPerspectiveCorrection 讀 state,
    // 直接在偵測/旋轉 callback 呼叫會用到舊角點),自動框選成功與按下旋轉後都走這條
    useEffect(() => {
        if (pendingAutoPreviewRef.current && currentPerspectivePoints) {
            pendingAutoPreviewRef.current = false;
            applyPerspectiveCorrection();
        }
    }, [currentPerspectivePoints, applyPerspectiveCorrection]);

    const drawMagnifier = useCallback((canvasPosition: { x: number; y: number }, force = false) => {
        if (!magnifierCanvasRef.current || !perspectiveCanvasRef.current || !showMagnifier) {
            return;
        }

        try {
            const magnifierCanvas = magnifierCanvasRef.current;
            const sourceCanvas = perspectiveCanvasRef.current;
            const sourceImage = magnifierSourceImageRef.current;
            const magnifierCtx = magnifierCanvas.getContext('2d');

            if (!magnifierCtx) return;
            if (!sourceImage) return;

            const threshold = 8;
            if (!force &&
                Math.abs(canvasPosition.x - magnifierCenter.x) < threshold &&
                Math.abs(canvasPosition.y - magnifierCenter.y) < threshold) {
                return;
            }

            setMagnifierCenter(canvasPosition);

            const magnifierSize = MAGNIFIER_SIZE;
            const zoomFactor = MAGNIFIER_ZOOM;
            const radius = magnifierSize / 2;

            magnifierCanvas.width = magnifierSize;
            magnifierCanvas.height = magnifierSize;

            magnifierCtx.imageSmoothingEnabled = true;
            magnifierCtx.imageSmoothingQuality = 'high';

            magnifierCtx.clearRect(0, 0, magnifierSize, magnifierSize);

            magnifierCtx.save();
            magnifierCtx.beginPath();
            magnifierCtx.arc(radius, radius, radius, 0, 2 * Math.PI);
            magnifierCtx.clip();

            const sampleSize = magnifierSize / zoomFactor;
            const sourceWidth = sourceImage.width || sourceCanvas.width;
            const sourceHeight = sourceImage.height || sourceCanvas.height;
            const sampleX = Math.max(0, Math.min(sourceWidth - sampleSize, canvasPosition.x - sampleSize / 2));
            const sampleY = Math.max(0, Math.min(sourceHeight - sampleSize, canvasPosition.y - sampleSize / 2));

            magnifierCtx.drawImage(
                sourceImage,
                sampleX, sampleY, sampleSize, sampleSize,
                0, 0, magnifierSize, magnifierSize
            );

            if (currentPerspectivePoints) {
                const scale = zoomFactor;
                const toMagnifier = (p: CornerPoint) => ({
                    x: (p.x - sampleX) * scale,
                    y: (p.y - sampleY) * scale
                });

                const tl = toMagnifier(currentPerspectivePoints.topLeft);
                const tr = toMagnifier(currentPerspectivePoints.topRight);
                const br = toMagnifier(currentPerspectivePoints.bottomRight);
                const bl = toMagnifier(currentPerspectivePoints.bottomLeft);

                magnifierCtx.strokeStyle = '#ef4444';
                magnifierCtx.lineWidth = 4;
                magnifierCtx.beginPath();
                magnifierCtx.moveTo(tl.x, tl.y);
                magnifierCtx.lineTo(tr.x, tr.y);
                magnifierCtx.lineTo(br.x, br.y);
                magnifierCtx.lineTo(bl.x, bl.y);
                magnifierCtx.closePath();
                magnifierCtx.stroke();
            }

            magnifierCtx.restore();

            magnifierCtx.strokeStyle = '#333';
            magnifierCtx.lineWidth = 3;
            magnifierCtx.beginPath();
            magnifierCtx.arc(radius, radius, radius - 1.5, 0, 2 * Math.PI);
            magnifierCtx.stroke();
        } catch (error) {
            console.error('放大鏡繪製失敗:', error);
        }
    }, [showMagnifier, magnifierCenter, currentPerspectivePoints, MAGNIFIER_SIZE, MAGNIFIER_ZOOM]);

    const updateMagnifierPosition = useCallback((canvasPos: { x: number; y: number }) => {
        if (magnifierUpdateTimeoutRef.current) {
            cancelAnimationFrame(magnifierUpdateTimeoutRef.current);
        }

        const viewW = typeof window !== 'undefined' ? window.innerWidth : MAGNIFIER_SIZE;
        const viewH = typeof window !== 'undefined' ? window.innerHeight : MAGNIFIER_SIZE;
        const rect = perspectiveCanvasRef.current?.getBoundingClientRect();
        const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

        const centerX = rect
            ? clamp(rect.left + rect.width / 2 - MAGNIFIER_SIZE / 2, 8, Math.max(8, viewW - MAGNIFIER_SIZE - 8))
            : Math.max(8, (viewW - MAGNIFIER_SIZE) / 2);
        const centerY = rect
            ? clamp(rect.top + rect.height / 2 - MAGNIFIER_SIZE / 2, 8, Math.max(8, viewH - MAGNIFIER_SIZE - 8))
            : Math.max(8, (viewH - MAGNIFIER_SIZE) / 2);

        setMagnifierPosition({ x: centerX, y: centerY });

        magnifierUpdateTimeoutRef.current = requestAnimationFrame(() => {
            drawMagnifier(canvasPos);
        });
    }, [drawMagnifier, MAGNIFIER_SIZE]);

    // 重新繪製透視校正畫布
    const redrawPerspectiveCanvas = useCallback(() => {
        const currentImageUrl = previewUrls[currentEditingImageIndex];
        if (!perspectiveCanvasRef.current || !currentPerspectivePoints || !currentImageUrl) return;

        const canvas = perspectiveCanvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // 拖曳時高頻觸發,必須「同步」繪製:走快取命中的路徑不會進 img.onload,
        // 才不會出現 canvas 短暫空白 → 角點閃一下就消失(國中 PerspectiveEditor 踩過的 bug)
        withCorrectionImage(currentImageUrl, selectedImages[currentEditingImageIndex], (img) => {
            // 計算容器可用尺寸
            const container = canvas.parentElement;
            if (!container) return;

            // 依累計角度從原圖重算繪製來源(0 度時就是原圖本身);同 url+角度會命中快取
            const source = getRotatedSource(currentImageUrl, img, correctionRotationRef.current);
            const sourceWidth = source.width;
            const sourceHeight = source.height;

            const containerRect = container.getBoundingClientRect();
            const maxWidth = containerRect.width - 32; // 減去 padding
            const maxHeight = containerRect.height - 32; // 減去 padding

            // 計算縮放比例以適應容器
            const scaleX = maxWidth / sourceWidth;
            const scaleY = maxHeight / sourceHeight;
            const scale = Math.min(scaleX, scaleY, 1); // 不放大，只縮小

            const displayWidth = sourceWidth * scale;
            const displayHeight = sourceHeight * scale;

            // 設置 canvas 顯示尺寸
            canvas.style.width = displayWidth + 'px';
            canvas.style.height = displayHeight + 'px';

            // 設置 canvas 實際尺寸（保持原始解析度；旋轉 90/270 度時寬高已互換）
            canvas.width = sourceWidth;
            canvas.height = sourceHeight;

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(source, 0, 0);
            magnifierSourceImageRef.current = source;

            if (showPerspectiveEditor && currentPerspectivePoints) {
                ctx.save();
                ctx.globalAlpha = 0.2;
                ctx.fillStyle = 'rgba(0, 0, 0, 1)';

                ctx.beginPath();
                ctx.rect(0, 0, canvas.width, canvas.height);
                ctx.moveTo(currentPerspectivePoints.topLeft.x, currentPerspectivePoints.topLeft.y);
                ctx.lineTo(currentPerspectivePoints.bottomLeft.x, currentPerspectivePoints.bottomLeft.y);
                ctx.lineTo(currentPerspectivePoints.bottomRight.x, currentPerspectivePoints.bottomRight.y);
                ctx.lineTo(currentPerspectivePoints.topRight.x, currentPerspectivePoints.topRight.y);
                ctx.closePath();
                ctx.fill('evenodd');
                ctx.restore();
            }

            ctx.strokeStyle = '#ef4444';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(currentPerspectivePoints.topLeft.x, currentPerspectivePoints.topLeft.y);
            ctx.lineTo(currentPerspectivePoints.topRight.x, currentPerspectivePoints.topRight.y);
            ctx.lineTo(currentPerspectivePoints.bottomRight.x, currentPerspectivePoints.bottomRight.y);
            ctx.lineTo(currentPerspectivePoints.bottomLeft.x, currentPerspectivePoints.bottomLeft.y);
            ctx.closePath();
            ctx.stroke();

            // 繪製角點（圖片已壓縮至長邊 1280px，半徑可固定）
            Object.entries(currentPerspectivePoints).forEach(([, point]) => {
                const outerRadius = isTouchDevice.current ? 50 : 16;
                const middleRadius = isTouchDevice.current ? 35 : 11;
                const innerRadius = isTouchDevice.current ? 18 : 6;

                ctx.fillStyle = '#ef4444';
                ctx.beginPath();
                ctx.arc(point.x, point.y, outerRadius, 0, 2 * Math.PI);
                ctx.fill();

                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.arc(point.x, point.y, middleRadius, 0, 2 * Math.PI);
                ctx.fill();

                ctx.fillStyle = '#ef4444';
                ctx.beginPath();
                ctx.arc(point.x, point.y, innerRadius, 0, 2 * Math.PI);
                ctx.fill();
            });
        });
    }, [currentPerspectivePoints, showPerspectiveEditor, previewUrls, currentEditingImageIndex, selectedImages, withCorrectionImage, getRotatedSource]);

    useEffect(() => {
        const hasMatchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
        const coarse = hasMatchMedia &&
            (window.matchMedia('(pointer: coarse)').matches ||
             window.matchMedia('(any-pointer: coarse)').matches);
        const fine = hasMatchMedia &&
            (window.matchMedia('(pointer: fine)').matches ||
             window.matchMedia('(any-pointer: fine)').matches);

        const nav = typeof navigator !== 'undefined'
            ? (navigator as Navigator & { msMaxTouchPoints?: number })
            : undefined;
        const touchPoints = nav
            ? Math.max(nav.maxTouchPoints || 0, nav.msMaxTouchPoints || 0)
            : 0;

        const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
        const isMobileUA = /Mobi|Android|iPhone|iPad|iPod|Tablet|Touch/i.test(ua);

        const prev = isTouchDevice.current;
        isTouchDevice.current = isMobileUA && (touchPoints > 0 || (coarse && !fine));

        if (prev !== isTouchDevice.current && currentPerspectivePoints && previewUrls[currentEditingImageIndex]) {
            redrawPerspectiveCanvas();
        }
    }, [currentPerspectivePoints, previewUrls, currentEditingImageIndex, redrawPerspectiveCanvas]);

    // 當編輯圖片索引改變時，只在該圖片沒有透視校正點時才初始化
    useEffect(() => {
        if (showPerspectiveEditor && previewUrls[currentEditingImageIndex]) {
            const currentImagePoints = perspectivePointsArray[currentEditingImageIndex];


            // 只有當該圖片還沒有透視校正點時才進行初始化
            if (!currentImagePoints) {
                const timeoutId = setTimeout(() => {
                    initializePerspectivePoints(currentEditingImageIndex);
                }, 200);

                return () => clearTimeout(timeoutId);
            } else {
                // 如果已有透視校正點，直接重新繪製 canvas
                const timeoutId = setTimeout(() => {
                    redrawPerspectiveCanvas();
                }, 200);

                return () => clearTimeout(timeoutId);
            }
        }
    }, [currentEditingImageIndex, showPerspectiveEditor, previewUrls, perspectivePointsArray, initializePerspectivePoints, redrawPerspectiveCanvas]);

    // 當透視點改變時重新繪製
    useEffect(() => {
        if (currentPerspectivePoints && showPerspectiveEditor) {
            redrawPerspectiveCanvas();
        }
    }, [currentPerspectivePoints, redrawPerspectiveCanvas, showPerspectiveEditor]);

    // 全域滑鼠事件監聽器（只監聽滑鼠事件，觸控事件直接綁定在 canvas 上）
    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (!draggedCorner || !currentPerspectivePoints || !perspectiveCanvasRef.current || !dragOffset) return;

            const canvas = perspectiveCanvasRef.current;
            const rect = canvas.getBoundingClientRect();

            // 計算從顯示尺寸到實際 canvas 尺寸的縮放比例
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;

            const mouseX = (e.clientX - rect.left) * scaleX;
            const mouseY = (e.clientY - rect.top) * scaleY;

            const x = Math.max(0, Math.min(canvas.width, mouseX - dragOffset.x));
            const y = Math.max(0, Math.min(canvas.height, mouseY - dragOffset.y));

            // 批量更新：使用 React 18 的批處理優化
            setPerspectivePoints({
                ...currentPerspectivePoints,
                [draggedCorner]: { x, y }
            });
            updateMagnifierPosition({ x, y });
        };

        const handleGlobalMouseUp = () => {
            setDraggedCorner(null);
            setDragOffset(null);
            setShowMagnifier(false);
            applyPerspectiveCorrection();
        };

        if (draggedCorner) {
            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('mouseup', handleGlobalMouseUp);
        };
    }, [draggedCorner, currentPerspectivePoints, dragOffset, setPerspectivePoints, setDraggedCorner, setDragOffset, updateMagnifierPosition, applyPerspectiveCorrection]);

    // 觸控事件處理函數（直接綁定在 canvas 上，與國中版一致）
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        e.preventDefault(); // 防止頁面滾動

        if (!draggedCorner || !currentPerspectivePoints || !perspectiveCanvasRef.current || !dragOffset) return;

        const touch = e.touches[0];
        const canvas = perspectiveCanvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // 計算從顯示尺寸到實際 canvas 尺寸的縮放比例
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const touchX = (touch.clientX - rect.left) * scaleX;
        const touchY = (touch.clientY - rect.top) * scaleY;

        const x = Math.max(0, Math.min(canvas.width, touchX - dragOffset.x));
        const y = Math.max(0, Math.min(canvas.height, touchY - dragOffset.y));

        // 直接更新狀態
        setPerspectivePoints({
            ...currentPerspectivePoints,
            [draggedCorner]: { x, y }
        });
        updateMagnifierPosition({ x, y });
    }, [draggedCorner, currentPerspectivePoints, dragOffset, setPerspectivePoints, updateMagnifierPosition]);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        e.preventDefault();

        // 清除放大鏡相關的動畫幀
        if (magnifierUpdateTimeoutRef.current) {
            cancelAnimationFrame(magnifierUpdateTimeoutRef.current);
            magnifierUpdateTimeoutRef.current = null;
        }
        setDraggedCorner(null);
        setDragOffset(null);
        setShowMagnifier(false);
        applyPerspectiveCorrection();
    }, [setDraggedCorner, setDragOffset, applyPerspectiveCorrection]);

    // 放大鏡繪製效果 - 高度優化性能
    useEffect(() => {
        if (showMagnifier && magnifierCenter.x !== 0 && magnifierCenter.y !== 0) {
            // 清除之前的 animationFrame
            if (magnifierUpdateTimeoutRef.current) {
                cancelAnimationFrame(magnifierUpdateTimeoutRef.current);
            }

            // 直接調用 drawMagnifier，移除不必要的 requestAnimationFrame
            // 因為 touchmove 中已經使用了 requestAnimationFrame 進行節流
            drawMagnifier({ x: magnifierCenter.x, y: magnifierCenter.y });
        }

        return () => {
            if (magnifierUpdateTimeoutRef.current) {
                cancelAnimationFrame(magnifierUpdateTimeoutRef.current);
            }
        };
    }, [showMagnifier, magnifierCenter.x, magnifierCenter.y, drawMagnifier]);

    // 組件卸載時強制清理放大鏡狀態
    useEffect(() => {
        return () => {
            // 確保組件卸載時清除所有放大鏡相關狀態
            setShowMagnifier(false);
            if (magnifierUpdateTimeoutRef.current) {
                cancelAnimationFrame(magnifierUpdateTimeoutRef.current);
                magnifierUpdateTimeoutRef.current = null;
            }
        };
    }, []);

    // 防止背景滾動
    useEffect(() => {
        if (showPerspectiveEditor) {
            // 防止背景滾動
            document.body.style.overflow = 'hidden';

            return () => {
                document.body.style.overflow = 'auto';
            };
        }
    }, [showPerspectiveEditor]);

    return (
        <>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 pb-2">
                作文圖片上傳
            </h2>

            <div>
                {/* 題目選擇區 */}
                <div className="mb-4">
                    <label
                        htmlFor="topic-select-image"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        選擇題目
                    </label>
                    <select
                        id="topic-select-image"
                        value={selectedTopic}
                        onChange={(e) => {
                            const chapterId = e.target.value;
                            const chapter = chapterOptions.find(ch => ch._id === chapterId);
                            handleSelectTopic(chapterId, chapter?.chapter || '');
                        }}
                        className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                    >
                        <option value="">-- 請選擇題目 --</option>
                        {chapterOptions.map((chapter) => (
                            <option
                                key={chapter._id}
                                value={chapter._id}
                            >
                                {chapter.chapter}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 顯示選擇的章節資訊 */}
                {selectedTopic && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-md">
                        <p className="font-semibold text-gray-700 mb-3">
                            已選擇章節：{chapterOptions.find(ch => ch._id === selectedTopic)?.chapter}
                        </p>
                        <div className="text-gray-700 text-sm leading-relaxed">
                            {(() => {
                                const desc = chapterOptions.find(ch => ch._id === selectedTopic)?.chapterDesc || '';
                                const selectedChapter = chapterOptions.find(ch => ch._id === selectedTopic);

                                return (
                                    <div>
                                        <TopicDesc
                                            desc={desc}
                                            images={selectedChapter?.images}
                                            chapterName={selectedChapter?.chapter ?? ''}
                                        />

                                        {/* 顯示章節圖片 */}
                                        {selectedChapter?.chapterDescUrl && (
                                            <div className="mt-4">
                                                <img
                                                    src={selectedChapter.chapterDescUrl}
                                                    alt={`${selectedChapter.chapter} - 章節圖片`}
                                                    className="w-full max-w-2xl mx-auto rounded-md border border-gray-200 shadow-sm"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}


                {/* 旋轉視窗(kanban 113663):flag 開啟時取代下方的四角拖曳視窗。
                    只做一件事 —— 把圖轉正;四角校正已搬進批改後端管線內部處理
                    (kanban 114186),前端不再打 /cornerdetect。 */}
                {rotateOnlyFlow && showPerspectiveEditor && previewUrls.length > 0 && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
                        {/* 背景遮罩 */}
                        <div className="absolute inset-0 bg-black bg-opacity-75" />

                        {/* 模態內容(無 touchAction:'none' —— 這裡沒有拖曳,擋掉手勢會讓手機無法捲動縮放) */}
                        <div
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="rotate-dialog-title"
                            className="relative w-[98vw] max-h-[95vh] lg:h-[88vh] flex flex-col bg-white rounded-lg shadow-lg overflow-hidden"
                        >
                            {/* 關閉按鈕 — 單純關窗,圖與目前角度都保留。
                                注意:自 kanban 106497 起「確認」改為逐張前進(confirmRotationStep),
                                所以這裡**不等同確認** —— 它放棄整個佇列,後面還沒檢查的頁不會再進視窗
                                (上限 2 張、視窗只在有新圖時才會再開,那些頁只剩預覽區縮圖的旋轉鈕可救)。
                                這是刻意的:明確的關窗手勢視為放棄整批,PM 沒有要求「跳過這張」。
                                不走 closePerspectiveEditor:那是舊四角視窗「放棄這張圖」的語意
                                (第一張會清掉整個上傳狀態),對只是想關掉視窗的使用者是驚嚇。
                                要刪圖請用上傳頁既有的移除機制。 */}
                            <button
                                onClick={confirmRotation}
                                aria-label="關閉"
                                className="absolute top-4 right-4 z-10 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            {/* 主要編輯區域 */}
                            <div className="flex-1 overflow-auto p-3 sm:p-5 lg:p-7">
                                <div className="h-full grid grid-cols-1 gap-3">
                                    <div className="flex flex-col h-full">
                                        <h3 id="rotate-dialog-title" className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-center text-gray-700">
                                            調整圖片方向
                                            {rotationCounter && (
                                                <span className="text-gray-400 text-sm ml-2">
                                                    {formatImageCounter(rotationCounter.index, rotationCounter.total)}
                                                </span>
                                            )}
                                        </h3>
                                        <div className="flex-1 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50 p-2 relative h-[55vh] lg:h-[400px] min-h-[280px]">
                                            {/* 處理中 chip:canvas 重繪套不了 CSS transition,用它蓋住重繪空窗 */}
                                            {isRotating && (
                                                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white/95 border border-blue-200 rounded-full px-3 py-1.5 shadow">
                                                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                                                    <span className="text-xs text-blue-700 font-medium">圖片處理中…</span>
                                                </div>
                                            )}
                                            <div className="absolute inset-2 flex items-center justify-center overflow-hidden">
                                                <canvas
                                                    ref={rotateCanvasRef}
                                                    className="max-w-full max-h-full object-contain"
                                                    style={{
                                                        width: 'auto',
                                                        height: 'auto',
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        objectFit: 'contain',
                                                        // flex item 的自動最小尺寸(min-*: auto)會以內容寬高為地板,
                                                        // 把它放掉才能保證 1280px 的 canvas 在窄螢幕上真的縮進容器、
                                                        // 不被 overflow-hidden 裁掉(舊視窗是自己算 px 尺寸,不靠 CSS fit)
                                                        minWidth: 0,
                                                        minHeight: 0,
                                                        // 硬體加速(沿用既有校正視窗的 canvas 設定)
                                                        transform: 'translateZ(0)',
                                                        backfaceVisibility: 'hidden',
                                                        WebkitBackfaceVisibility: 'hidden'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {/* 視覺隱藏的角度播報 */}
                                        <span className="sr-only" aria-live="polite">{rotationAnnouncement}</span>
                                    </div>
                                </div>
                            </div>

                            {/* 底部操作按鈕 */}
                            <div className="bg-gray-100 border-t px-3 sm:px-6 py-3 sm:py-4">
                                {/* 操作提醒 */}
                                <p className="text-xs sm:text-sm font-bold text-center mb-2 sm:mb-4" style={{ color: '#254D71' }}>
                                    操作提醒：請將圖片轉正，讓文字能正常閱讀後再按確認。
                                </p>
                                <div className="flex justify-center gap-2 sm:gap-4 flex-wrap">
                                    <Tooltip title="向右旋轉90度" arrow placement="top">
                                        {/* disabled 的 MUI Button 不觸發事件,Tooltip 要靠這層 span 接 hover */}
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<RotateRightIcon />}
                                                onClick={handleRotateInDialog}
                                                disabled={isRotating}
                                                color="primary"
                                                size="medium"
                                                sx={{ backgroundColor: 'white' }}
                                                aria-label="向右旋轉90度"
                                            >
                                                旋轉90度
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    {/* 確認 = 處理完這張、換下一張;整批走完才關窗(kanban 106497)。
                                        走 confirmRotationStep 而非 confirmRotation —— 後者直接關窗,
                                        多頁 PDF 的第 1 頁就永遠沒機會轉正。 */}
                                    <Button
                                        ref={confirmRotationButtonRef}
                                        variant="contained"
                                        startIcon={<CheckIcon />}
                                        onClick={() => {
                                            // 換頁到下一張前先鎖住:新圖要等 img 解碼才畫進 canvas,
                                            // 這段空窗期畫布還是上一張,兩顆鈕若可按,使用者會以為
                                            // 看到的是下一張而轉錯張(kanban 106497 code review)。
                                            // 重繪完成時 draw effect 的 release() 會解鎖。
                                            rotatingRef.current = true;
                                            setIsRotating(true);
                                            confirmRotationStep();
                                        }}
                                        disabled={isRotating}
                                        color="primary"
                                        size="medium"
                                    >
                                        {isRotating ? '處理中...' : '確認'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 透視校正模態視窗(「調整邊界」,rotateOnlyFlow 關閉時使用;
                    kanban 1003418 起恢復為現行動線,旋轉視窗保留以便切回) */}
                {!rotateOnlyFlow && showPerspectiveEditor && previewUrls.length > 0 && (
                    // key 跟著 group(kanban 1003418):第 1 張按 X 接著開第 2 張時視窗不會關,
                    // 不換 key 的話右側「校正後預覽」畫布還留著第 1 張的校正結果
                    <div key={currentProcessingGroupId ?? undefined} className="fixed inset-0 z-50 flex items-center justify-center p-2">
                        {/* 背景遮罩 */}
                        <div className="absolute inset-0 bg-black bg-opacity-75" />

                        {/* 模態內容 */}
                        <div className="relative w-[98vw] max-h-[95vh] lg:h-[88vh] flex flex-col bg-white rounded-lg shadow-lg overflow-hidden" style={{ touchAction: 'none' }}>
                            {/* 關閉按鈕 */}
                            <button
                                onClick={closePerspectiveEditor}
                                className="absolute top-4 right-4 z-10 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>


                            {/* 主要編輯區域 */}
                            <div className="flex-1 overflow-auto p-3 sm:p-5 lg:p-7">
                                <div className="h-full grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    {/* 左側：編輯區域 */}
                                    <div className="flex flex-col h-full">
                                        <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-center text-gray-700">調整邊界</h3>
                                    <div className="flex-1 border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50 p-2 relative h-[55vh] lg:h-[400px] min-h-[280px]" style={{ touchAction: 'none' }}>
                                        {/* 自動框選中提示(打 /cornerdetect 預填角點的期間顯示,全環境) */}
                                        {isDetectingCorners && (
                                            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-white/95 border border-blue-200 rounded-full px-3 py-1.5 shadow">
                                                <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                                                <span className="text-xs text-blue-700 font-medium">AI 自動框選中…</span>
                                            </div>
                                        )}
                                        <div className="absolute inset-2 flex items-center justify-center overflow-hidden">
                                            <canvas
                                                ref={perspectiveCanvasRef}
                                                className="max-w-full max-h-full object-contain"
                                                style={{
                                                    cursor: draggedCorner ? 'grabbing' : 'grab',
                                                    width: 'auto',
                                                    height: 'auto',
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain',
                                                    touchAction: 'none',
                                                    WebkitTouchCallout: 'none',
                                                    WebkitUserSelect: 'none',
                                                    userSelect: 'none',
                                                    // 添加硬體加速和優化
                                                    willChange: draggedCorner ? 'transform' : 'auto',
                                                    transform: 'translateZ(0)', // 啟用硬體加速
                                                    backfaceVisibility: 'hidden',
                                                    WebkitBackfaceVisibility: 'hidden'
                                                }}
                                                onContextMenu={(e) => e.preventDefault()}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    if (!currentPerspectivePoints || !perspectiveCanvasRef.current) return;

                                                        const canvas = perspectiveCanvasRef.current;
                                                        const rect = canvas.getBoundingClientRect();

                                                        // 計算從顯示尺寸到實際 canvas 尺寸的縮放比例
                                                        const scaleX = canvas.width / rect.width;
                                                        const scaleY = canvas.height / rect.height;

                                                        const x = (e.clientX - rect.left) * scaleX;
                                                        const y = (e.clientY - rect.top) * scaleY;

                                                        const corners = Object.entries(currentPerspectivePoints) as [keyof PerspectivePoints, CornerPoint][];
                                                        let closestCorner: keyof PerspectivePoints | null = null;
                                                        let minDistance = Infinity;
                                                        const clickThreshold = 35;

                                                        corners.forEach(([corner, point]) => {
                                                            const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
                                                            if (distance < minDistance) {
                                                                minDistance = distance;
                                                                closestCorner = corner;
                                                            }
                                                        });

                                                        if (minDistance > clickThreshold) {
                                                            closestCorner = null;
                                                        }

                                                        if (closestCorner && currentPerspectivePoints) {
                                                            const cornerPoint = currentPerspectivePoints[closestCorner] as CornerPoint;
                                                            const offsetX = x - cornerPoint.x;
                                                            const offsetY = y - cornerPoint.y;

                                                            userAdjustedCornersRef.current = true; // 使用者開始手動調整,自動框選結果不再覆寫
                                                            setDraggedCorner(closestCorner);
                                                            setDragOffset({ x: offsetX, y: offsetY });
                                                            setShowMagnifier(true);
                                                            updateMagnifierPosition({ x, y });
                                                        }
                                                    }}
                                                    onTouchStart={(e) => {
                                                        if (!currentPerspectivePoints || !perspectiveCanvasRef.current) return;

                                                        const touch = e.touches[0];
                                                        const canvas = perspectiveCanvasRef.current;
                                                        const rect = canvas.getBoundingClientRect();

                                                        // 計算從顯示尺寸到實際 canvas 尺寸的縮放比例
                                                        const scaleX = canvas.width / rect.width;
                                                        const scaleY = canvas.height / rect.height;

                                                        const x = (touch.clientX - rect.left) * scaleX;
                                                        const y = (touch.clientY - rect.top) * scaleY;

                                                        const corners = Object.entries(currentPerspectivePoints) as [keyof PerspectivePoints, CornerPoint][];
                                                        let closestCorner: keyof PerspectivePoints | null = null;
                                                        let minDistance = Infinity;
                                                        const clickThreshold = 80;

                                                        corners.forEach(([corner, point]) => {
                                                            const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
                                                            if (distance < minDistance) {
                                                                minDistance = distance;
                                                                closestCorner = corner;
                                                            }
                                                        });

                                                        if (minDistance > clickThreshold) {
                                                            closestCorner = null;
                                                        }

                                                        if (closestCorner && currentPerspectivePoints) {
                                                            const cornerPoint = currentPerspectivePoints[closestCorner] as CornerPoint;
                                                            const offsetX = x - cornerPoint.x;
                                                            const offsetY = y - cornerPoint.y;

                                                            userAdjustedCornersRef.current = true; // 使用者開始手動調整,自動框選結果不再覆寫
                                                            setDraggedCorner(closestCorner);
                                                            setDragOffset({ x: offsetX, y: offsetY });
                                                            setShowMagnifier(true);
                                                            updateMagnifierPosition({ x, y });
                                                        }
                                                    }}
                                                    onTouchMove={handleTouchMove}
                                                    onTouchEnd={handleTouchEnd}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 右側：校正後預覽 */}
                                    <div className="flex flex-col h-full">
                                        <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-center text-gray-700">校正後預覽</h3>
                                        <div className="flex-1 border-2 border-blue-300 rounded-lg overflow-hidden bg-blue-50 p-2 relative h-[35vh] lg:h-[400px] min-h-[200px]">
                                            <div className="absolute inset-2 flex items-center justify-center overflow-hidden">
                                                <canvas
                                                    ref={perspectivePreviewRef}
                                                    className="max-w-full max-h-full object-contain"
                                                    style={{
                                                        display: currentPerspectivePoints ? 'block' : 'none',
                                                        width: 'auto',
                                                        height: 'auto',
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                                {!currentPerspectivePoints && (
                                                    <div className="text-gray-400 text-center">
                                                        <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        <p>調整左側圖片後，預覽將顯示在這裡</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 底部操作按鈕 */}
                            <div className="bg-gray-100 border-t px-6 py-4">
                                {/* 操作提醒 */}
                                <p className="text-sm font-bold text-center mb-4" style={{ color: '#254D71' }}>
                                    操作提醒：為提升辨識度，請先確認紙張方向，再拖曳四個角框選書寫範圍，完成圖片校正。
                                </p>
                                {/* 按鈕順序「旋轉90度 → 重設角點 → 下一步」(kanban 1003418):
                                    與上方提醒的步驟一致 —— 先確認紙張方向,再框選書寫範圍 */}
                                <div className="flex justify-center gap-2 sm:gap-4 flex-wrap">
                                    <Tooltip title="向右旋轉90度" arrow placement="top">
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<RotateRightIcon />}
                                                onClick={handleRotateCorrection}
                                                disabled={isDetectingCorners}
                                                color="primary"
                                                size="medium"
                                                sx={{ backgroundColor: 'white' }}
                                                aria-label="向右旋轉90度"
                                            >
                                                旋轉90度
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RestartAltIcon />}
                                        onClick={handleResetCorners}
                                        color="primary"
                                        size="medium"
                                        sx={{ backgroundColor: 'white' }}
                                    >
                                        重設角點
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<ArrowForwardIcon />}
                                        onClick={() => {
                                            if (currentProcessedImageUrl) {
                                                // 四角標註(kanban 1003468):這一刻的角點與這一輪的紀錄。
                                                // 畫布尺寸對不上這一輪(換張後還沒重新初始化)就不記;
                                                // 任何例外都不能擋住「下一步」
                                                let cornerLabel: CornerLabelDraft | null = null;
                                                try {
                                                    const session = cornerSessionRef.current;
                                                    const canvas = perspectiveCanvasRef.current;
                                                    if (
                                                        session &&
                                                        currentPerspectivePoints &&
                                                        canvas &&
                                                        canvas.width === session.imageWidth &&
                                                        canvas.height === session.imageHeight
                                                    ) {
                                                        cornerLabel = buildCornerLabelDraft({
                                                            session,
                                                            finalPoints: currentPerspectivePoints,
                                                            rotation: correctionRotationRef.current,
                                                            touched: userAdjustedCornersRef.current,
                                                        });
                                                    }
                                                } catch (error) {
                                                    console.warn("[cornerlabel] 標註紀錄失敗(不影響上傳):", error);
                                                }
                                                // 應用處理後的圖片(帶上當下的 group,換張後晚到的寫回會被丟掉)
                                                applyProcessedImage(currentProcessedImageUrl, currentProcessingGroupId, cornerLabel);
                                                // 進入分割模式
                                                onEnterCropMode();
                                            }
                                        }}
                                        disabled={!currentProcessedImageUrl}
                                        color="primary"
                                        size="medium"
                                    >
                                        下一步
                                    </Button>
                                </div>
                            </div>

                            {/* 放大鏡 */}
                            {showMagnifier && (
                                <div
                                    className="fixed pointer-events-none z-[60]"
                                    style={{
                                        left: `${magnifierPosition.x}px`,
                                        top: `${magnifierPosition.y}px`,
                                        width: `${MAGNIFIER_SIZE}px`,
                                        height: `${MAGNIFIER_SIZE}px`
                                    }}
                                >
                                    <canvas
                                        ref={magnifierCanvasRef}
                                        className="rounded-full shadow-lg border-2 border-gray-800"
                                        style={{
                                            width: `${MAGNIFIER_SIZE}px`,
                                            height: `${MAGNIFIER_SIZE}px`,
                                            background: 'white',
                                            // 硬體加速優化
                                            willChange: 'transform',
                                            transform: 'translateZ(0)',
                                            backfaceVisibility: 'hidden',
                                            WebkitBackfaceVisibility: 'hidden'
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* 根據 processedGroups 決定布局 */}
                {processedGroups.length === 0 ? (
                    // 沒有圖片時：顯示單一上傳區塊
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        <div className="mb-6 text-gray-500">
                            <svg
                                className="mx-auto h-16 w-16 text-gray-400"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <p className="mt-2">上傳作文圖片</p>
                            <p className="text-sm text-gray-400 mt-1">
                                支援 JPG、PNG、GIF、PDF 格式（PDF 每頁算 1 張），每張圖片不超過 10MB<br />
                                請盡量避免陰影、反光，或使用掃描可獲得最佳體驗
                            </p>
                        </div>

                        <div className="flex flex-col items-center">
                            <label
                                htmlFor="image-upload"
                                className="bg-blue-500 hover:bg-blue-600 text-white cursor-pointer py-2 px-6 rounded-md transition-colors inline-block"
                                onClick={(e) => {
                                    if (isSubmitting || isProcessing) {
                                        e.preventDefault();
                                        return;
                                    }
                                    
                                    trackEvent("image_upload_button_clicked", {
                                        has_images: selectedImages.length > 0,
                                        image_count: selectedImages.length,
                                    });
                                }}
                            >
                                選擇圖片
                            </label>
                            <input
                                ref={fileInputRef}
                                id="image-upload"
                                type="file"
                                accept="image/*,.pdf,application/pdf"
                                onChange={handleImageChangeWithReset}
                                className="hidden"
                                disabled={isSubmitting || isProcessing}
                            />
                            {imageError && <p className="text-red-500 mt-2">{imageError}</p>}
                        </div>
                    </div>
                ) : (
                    // 有圖片時：根據數量決定布局
                    <div className={`grid gap-6 ${processedGroups.length === 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-2'}`}>
                        {/* 動態渲染每個圖片組 */}
                        {processedGroups.map((group, groupIndex) => (
                            <div key={groupIndex} className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                                <div className="mb-4">
                                    {/* 顯示校正後的圖片或原始圖片。
                                        用 canvas(RotatedThumbnail)而不是 <img> + CSS transform:
                                        transform 不改 layout box,90/270 度時溢出的圖會蓋住下方
                                        「重設圖片」按鈕(點不到);canvas 在 90/270 已互換寬高,
                                        max-h/max-w 讓瀏覽器直接 fit 進原容器,容器與按鈕都不用動。 */}
                                    <div className="relative mx-auto max-w-lg">
                                        <RotatedThumbnail
                                            src={group.correctedUrl || group.originalUrl}
                                            angle={imageRotations[group.id] || 0}
                                            label={`第${groupIndex + 1}張作文圖片`}
                                            className="max-h-[min(384px,80vw)] max-w-full mx-auto rounded border border-gray-300 shadow-sm"
                                        />
                                        {/* 旋轉按鈕:圓形圖示鍵疊在圖片下緣置中。角度記在 imageRotations,
                                            交卷時由 bakeRotationIntoImage 烤進送出的檔案(kanban 113663),
                                            不只是畫面上的檢視。
                                            四角校正動線下,已完成校正+分段(isProcessed)的圖不顯示(kanban 1003418):
                                            這時送出的是切好的細條,角度會逐條各自烤進去,轉出來不是整頁轉正;
                                            方向在調整邊界視窗裡轉,要重來用下面的「重設圖片」。
                                            未分段的圖、以及旋轉動線,照舊顯示。 */}
                                        {(rotateOnlyFlow || !group.isProcessed) && (
                                            <div className="group/rotate absolute bottom-3 left-1/2 -translate-x-1/2">
                                                <button
                                                    type="button"
                                                    onClick={() => rotateImage(group.id)}
                                                    disabled={isSubmitting}
                                                    aria-label="向右旋轉90度"
                                                    className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-gray-600 hover:text-[#2D86FF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8M21 3v5h-5" />
                                                    </svg>
                                                </button>
                                                <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-sm opacity-0 group-hover/rotate:opacity-100 transition-opacity z-10">
                                                    向右旋轉 90°
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button 
                                    onClick={() => removeProcessedGroup(groupIndex)}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer"
                                >
                                    重設圖片
                                </button>
                            </div>
                        ))}

                        {/* 如果只有一張圖片，右側顯示第二張上傳區域 */}
                        {processedGroups.length === 1 && (
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-center">
                                <div className="mb-6 text-gray-500">
                                    <svg
                                        className="mx-auto h-16 w-16 text-gray-400"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                    <p className="mt-2">上傳第二張圖片</p>
                                    <p className="text-sm text-gray-400 mt-1">
                                        支援 JPG、PNG、GIF、PDF 格式（PDF 每頁算 1 張），每張圖片不超過 10MB<br />
                                        請盡量避免陰影、反光，或使用掃描可獲得最佳體驗
                                    </p>
                                </div>

                                <div>
                                    {originalImageCount < 2 && (
                                        <label
                                            htmlFor="second-image-upload"
                                            className="bg-blue-500 hover:bg-blue-600 text-white cursor-pointer py-2 px-6 rounded-md transition-colors inline-block"
                                            onClick={(e) => {
                                                if (isSubmitting || isProcessing) {
                                                    e.preventDefault();
                                                    return;
                                                }
                                                
                                                trackEvent("second_image_upload_button_clicked", {
                                                    has_images: selectedImages.length > 0,
                                                    image_count: selectedImages.length,
                                                });
                                            }}
                                        >
                                            選擇圖片
                                        </label>
                                    )}
                                    <input
                                        id="second-image-upload"
                                        type="file"
                                        accept="image/*,.pdf,application/pdf"
                                        onChange={handleImageChangeWithReset}
                                        className="hidden"
                                        disabled={isSubmitting || isProcessing || originalImageCount >= 2}
                                    />
                                    
                                    {originalImageCount >= 2 && (
                                        <p className="text-green-600 font-medium">第二張圖片已上傳</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 已經有圖片時的錯誤出口(kanban 1000553)。
                    上方那個 imageError 紅字只長在「還沒有任何圖片」的空狀態區塊裡,
                    老師傳了第一張、再加第二張被擋時,訊息算得出來、setImageState 也
                    寫進去了,畫面卻一個字都不會出現 —— 就是本卡要修掉的「什麼都沒發生」。
                    條件與上面互斥(那邊是 length === 0),不會兩處同時顯示。 */}
                {processedGroups.length > 0 && imageError && (
                    <p className="text-red-500 mt-4 text-center">{imageError}</p>
                )}

                {/* 圖片數量提示 */}
                {originalImageCount > 0 && originalImageCount < 2 && (
                    <p className="text-center text-sm text-gray-500 mt-4">
                        已上傳 {originalImageCount} 張原始圖片，最多可上傳 2 張圖片
                    </p>
                )}
            </div>
        </>
    );
}

// 文字上傳區域元件
function TextUploadSection({
    essayText,
    selectedTopic,
    textError,
    isSubmitting,
    isProcessing,
    handleTextChange,
    handleSelectTopic,
    chapterOptions,
}: {
    essayText: string;
    selectedTopic: string;
    textError: string | null;
    isSubmitting: boolean;
    isProcessing: boolean;
    handleTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    handleSelectTopic: (chapterId: string, chapterName: string) => void;
    chapterOptions: Chapter[];
}) {
    const { trackEvent } = useGAEventTracker();

    useEffect(() => {
        trackEvent("view_section", {
            section_name: "text_upload",
        });
    }, [trackEvent]);

    const onTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        handleTextChange(e);

        const currentLength = e.target.value.length;
        const prevLength = essayText.length;

        if (
            Math.abs(currentLength - prevLength) >= 100 ||
            (prevLength === 0 && currentLength > 0) ||
            (prevLength > 0 && currentLength === 0)
        ) {
            trackEvent("essay_text_input", {
                text_length: currentLength,
                is_first_input: prevLength === 0 && currentLength > 0,
                is_clearing: prevLength > 0 && currentLength === 0,
            });
        }
    };

    return (
        <>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 pb-2">
                作文文字輸入
            </h2>

            <div>
                {/* 題目選擇區 */}
                <div className="mb-4">
                    <label
                        htmlFor="topic-select"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        選擇題目
                    </label>
                    <select
                        id="topic-select"
                        value={selectedTopic}
                        onChange={(e) => {
                            const chapterId = e.target.value;
                            const chapter = chapterOptions.find(ch => ch._id === chapterId);
                            handleSelectTopic(chapterId, chapter?.chapter || '');
                        }}
                        className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isSubmitting}
                    >
                        <option value="">-- 請選擇題目 --</option>
                        {chapterOptions.map((chapter) => (
                            <option
                                key={chapter._id}
                                value={chapter._id}
                            >
                                {chapter.chapter}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 顯示選擇的章節資訊 */}
                {selectedTopic && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-md">
                        <p className="font-semibold text-gray-700 mb-3">
                            已選擇章節：{chapterOptions.find(ch => ch._id === selectedTopic)?.chapter}
                        </p>
                        <div className="text-gray-700 text-sm leading-relaxed">
                            {(() => {
                                const desc = chapterOptions.find(ch => ch._id === selectedTopic)?.chapterDesc || '';
                                const selectedChapter = chapterOptions.find(ch => ch._id === selectedTopic);

                                return (
                                    <div>
                                        <TopicDesc
                                            desc={desc}
                                            images={selectedChapter?.images}
                                            chapterName={selectedChapter?.chapter ?? ''}
                                        />

                                        {/* 顯示章節圖片 */}
                                        {selectedChapter?.chapterDescUrl && (
                                            <div className="mt-4">
                                                <img
                                                    src={selectedChapter.chapterDescUrl}
                                                    alt={`${selectedChapter.chapter} - 章節圖片`}
                                                    className="w-full max-w-2xl mx-auto rounded-md border border-gray-200 shadow-sm"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.currentTarget.style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                <label
                    htmlFor="essay-content"
                    className="block text-sm font-medium text-gray-700 mb-1"
                >
                    作文內容
                </label>
                <textarea
                    id="essay-content"
                    placeholder={
                        isProcessing 
                            ? "評改中，請等待完成後再上傳新的作文..."
                            : selectedTopic
                                ? `請依照題目要求撰寫作文...`
                                : "請在此輸入您的作文內容..."
                    }
                    value={essayText}
                    onChange={onTextAreaChange}
                    disabled={isSubmitting || isProcessing}
                    className={`w-full h-64 p-2 border rounded-md focus:outline-none resize-none ${
                        isSubmitting || isProcessing 
                            ? "bg-gray-100 cursor-not-allowed" 
                            : "focus:ring-2 focus:ring-blue-500"
                    }`}
                />

                {textError && <p className="text-red-500 mt-2">{textError}</p>}

                <div className="mt-2 text-right text-gray-500 text-sm">
                    {essayText.length} 字
                </div>
            </div>
        </>
    );
}


// 錯誤彈出視窗元件
function ErrorPopup({ message, onClose }: { message: string; onClose: () => void }) {
    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-50"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.75)" }}
        >
            <div className="bg-white rounded-lg shadow-md max-w-md w-full mx-4 overflow-hidden border border-gray-200">
                <div className="bg-[#254D71] px-4 py-3 text-white flex justify-between items-center">
                    <h3 className="font-medium text-lg">提示</h3>
                    <button onClick={onClose} className="text-white hover:text-gray-200">
                        <svg
                            className="h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-6 w-6 text-[#2D86FF]"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-gray-700">{message}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2D86FF] text-base font-medium text-white hover:bg-blue-600 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                    >
                        確認
                    </button>
                </div>
            </div>
        </div>
    );
}
